import React, { useState, useEffect, useMemo, useContext, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Boxes, Search, Save, Loader, X, Check, AlertTriangle, AlertCircle,
  Building2, Package, History, ChevronLeft, ChevronRight,
  TrendingDown, TrendingUp, Users, FileSpreadsheet, Upload, Download,
  Plus, FileEdit, Eye, Trash2, Calendar, HelpCircle, RefreshCw, FileText, Truck,
  CircleDot
} from 'lucide-react';
import { AuthContext } from '../App';
import { useTheme } from '../contexts/ThemeContext';
import AlbaranMixtoAceptarModal from '../components/AlbaranMixtoAceptarModal';

const API_URL = process.env.REACT_APP_API_URL;

const useSafeTheme = () => {
  const { theme } = useTheme();
  return {
    primary:       theme?.primary       || '#3cbf9a',
    primaryDark:   theme?.primaryDark   || '#2da080',
    primaryLight:  theme?.primaryLight  || 'rgba(60, 191, 154, 0.10)',
    primaryBorder: theme?.primaryBorder || 'rgba(60, 191, 154, 0.20)',
  };
};

const authHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json',
});

const fmtFecha = (iso) => iso ? new Date(iso).toLocaleString('es-ES') : '—';

/**
 * Traduce el tipo técnico del movimiento + método a un texto en español
 * comprensible. El campo `metodo` distingue cómo se hizo:
 *   'manual'  → web bulk, edición de lote, salida manual app
 *   'pistola' → escaneo con pistola física
 *   'camara'  → escaneo con cámara del móvil
 *   'albaran' → flujo de albarán (proveedor)
 *   'excel'   → carga inicial por Excel
 */
const labelMovimiento = (m) => {
  // Casos especiales por método
  if (m.metodo === 'excel')   return 'Carga inicial por Excel';
  if (m.metodo === 'albaran') return 'Entrada por albarán';

  // Fallback por motivo (movimientos antiguos sin método)
  const motivoTxt = String(m.motivo || '').toLowerCase();
  if (motivoTxt.includes('carga inicial')) return 'Carga inicial';

  const sufijoMetodo =
    m.metodo === 'pistola' ? ' por pistola'
    : m.metodo === 'camara' ? ' por cámara móvil'
    : m.metodo === 'manual' ? ' manual'
    : (m.lote_id ? ' por escaneo' : ' manual');

  if (m.tipo === 'entrada') {
    if (m.pedido_id && !m.metodo) return 'Entrada por albarán';
    return `Entrada${sufijoMetodo}`;
  }
  if (m.tipo === 'salida')       return `Salida${sufijoMetodo}`;
  if (m.tipo === 'ajuste')       return `Ajuste${sufijoMetodo}`;
  if (m.tipo === 'set_inicial')  return 'Stock inicial';
  return m.tipo;
};

// ─── Modal de historial de movimientos ────────────────────────────────────────
// ─── Panel: Movimientos (pestaña con filtros) ────────────────────────────────
const MovimientosPanel = ({ movimientos, loading, theme, filtroProductoExterno, onClearFiltroExterno }) => {
  const [busqueda, setBusqueda] = useState('');
  const [tipoFiltro, setTipoFiltro] = useState('todos');     // todos | entrada | salida | ajuste | set_inicial
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');

  // Si nos llega un filtro externo por producto (al hacer click en un producto
  // desde la tabla de stock), inicializamos la búsqueda con su nombre.
  useEffect(() => {
    if (filtroProductoExterno) setBusqueda(filtroProductoExterno);
  }, [filtroProductoExterno]);

  const filtrados = useMemo(() => {
    let arr = movimientos || [];
    const t = busqueda.trim().toUpperCase();
    if (t) {
      arr = arr.filter(m =>
        (m.producto_nombre || '').toUpperCase().includes(t) ||
        (m.ref_proveedor   || '').toUpperCase().includes(t) ||
        (m.lote_numero     || '').toUpperCase().includes(t) ||
        (m.usuario_nombre  || '').toUpperCase().includes(t) ||
        (m.motivo          || '').toUpperCase().includes(t)
      );
    }
    if (tipoFiltro !== 'todos') {
      arr = arr.filter(m => m.tipo === tipoFiltro);
    }
    if (fechaDesde) {
      const ts = new Date(fechaDesde).getTime();
      arr = arr.filter(m => new Date(m.fecha).getTime() >= ts);
    }
    if (fechaHasta) {
      const ts = new Date(fechaHasta).getTime() + 86400000; // incluir todo el día
      arr = arr.filter(m => new Date(m.fecha).getTime() < ts);
    }
    return arr;
  }, [movimientos, busqueda, tipoFiltro, fechaDesde, fechaHasta]);

  const limpiarFiltros = () => {
    setBusqueda(''); setTipoFiltro('todos'); setFechaDesde(''); setFechaHasta('');
    if (onClearFiltroExterno) onClearFiltroExterno();
  };

  const hayFiltros = busqueda || tipoFiltro !== 'todos' || fechaDesde || fechaHasta;

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Filtros */}
      <div className="px-4 py-2.5 border-b bg-gray-50/50 flex flex-wrap gap-2 flex-shrink-0">
        <div className="relative flex-1 min-w-[220px]">
          <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none">
            <Search size={14} className="text-gray-400" />
          </div>
          <input
            type="text"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Buscar producto, ref., lote, usuario o motivo..."
            className="w-full pr-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none"
            style={{ paddingLeft: '32px' }}
            onFocus={(e) => e.target.style.borderColor = theme.primary}
            onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
          />
        </div>
        <select
          value={tipoFiltro}
          onChange={(e) => setTipoFiltro(e.target.value)}
          className="px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none bg-white"
        >
          <option value="todos">Todos los tipos</option>
          <option value="entrada">Entradas</option>
          <option value="salida">Salidas</option>
          <option value="ajuste">Ajustes manuales</option>
          <option value="set_inicial">Stock inicial</option>
        </select>
        <div className="flex items-center gap-1 text-xs text-gray-500">
          <span>Desde:</span>
          <input
            type="date"
            value={fechaDesde}
            onChange={(e) => setFechaDesde(e.target.value)}
            className="px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none bg-white"
          />
          <span>Hasta:</span>
          <input
            type="date"
            value={fechaHasta}
            onChange={(e) => setFechaHasta(e.target.value)}
            className="px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none bg-white"
          />
        </div>
        {hayFiltros && (
          <button
            onClick={limpiarFiltros}
            className="px-3 py-1.5 text-xs text-gray-600 bg-white border border-gray-300 rounded hover:bg-gray-50"
          >
            Limpiar filtros
          </button>
        )}
      </div>

      {/* Tabla scrolleable */}
      <div className="flex-1 overflow-auto">
        {loading ? (
          <div className="p-12 text-center text-gray-400"><Loader size={24} className="animate-spin mx-auto" /></div>
        ) : filtrados.length === 0 ? (
          <div className="p-12 text-center text-gray-400 text-sm">
            {(movimientos || []).length === 0
              ? 'Sin movimientos registrados todavía.'
              : 'No hay movimientos que coincidan con los filtros.'}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b sticky top-0 z-10">
              <tr>
                <th className="text-left px-3 py-2 font-semibold text-gray-700">Fecha</th>
                <th className="text-left px-3 py-2 font-semibold text-gray-700">Producto</th>
                <th className="text-left px-3 py-2 font-semibold text-gray-700">Tipo</th>
                <th className="text-right px-3 py-2 font-semibold text-gray-700">Antes</th>
                <th className="text-right px-3 py-2 font-semibold text-gray-700">Después</th>
                <th className="text-right px-3 py-2 font-semibold text-gray-700">Δ</th>
                <th className="text-left px-3 py-2 font-semibold text-gray-700">Lote</th>
                <th className="text-left px-3 py-2 font-semibold text-gray-700">Vencimiento</th>
                <th className="text-left px-3 py-2 font-semibold text-gray-700">Pedido</th>
                <th className="text-left px-3 py-2 font-semibold text-gray-700">Usuario</th>
              </tr>
            </thead>
            <tbody>
              {filtrados.map(m => (
                <tr key={m.id} className="border-t hover:bg-gray-50">
                  <td className="px-3 py-2 text-gray-600 text-xs whitespace-nowrap">{fmtFecha(m.fecha)}</td>
                  <td className="px-3 py-2 text-gray-800 max-w-xs truncate" title={m.producto_nombre}>{m.producto_nombre}</td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      m.tipo === 'entrada'        ? 'bg-emerald-100 text-emerald-700'
                        : m.tipo === 'salida'      ? 'bg-red-100 text-red-700'
                        : m.tipo === 'set_inicial' ? 'bg-blue-100 text-blue-700'
                        : m.tipo === 'ajuste'      ? 'bg-amber-100 text-amber-700'
                        :                            'bg-gray-100 text-gray-700'
                    }`}>{labelMovimiento(m)}</span>
                  </td>
                  <td className="px-3 py-2 text-right text-gray-500">{m.cantidad_anterior ?? '—'}</td>
                  <td className="px-3 py-2 text-right font-semibold">{m.cantidad_nueva ?? '—'}</td>
                  <td className={`px-3 py-2 text-right font-bold ${
                    m.delta > 0 ? 'text-emerald-600' : m.delta < 0 ? 'text-red-600' : 'text-gray-400'
                  }`}>{m.delta > 0 ? `+${m.delta}` : m.delta || '—'}</td>
                  <td className="px-3 py-2 text-gray-700 text-xs font-mono">{m.lote_numero || '—'}</td>
                  <td className="px-3 py-2 text-gray-600 text-xs whitespace-nowrap">
                    {m.lote_fecha_vencimiento
                      ? new Date(m.lote_fecha_vencimiento).toLocaleDateString('es-ES')
                      : '—'}
                  </td>
                  <td className="px-3 py-2 text-gray-600 text-xs">{m.pedido_npedido || '—'}</td>
                  <td className="px-3 py-2 text-gray-600 text-xs">{m.usuario_nombre || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="border-t px-4 py-2 bg-gray-50 text-xs text-gray-600 flex-shrink-0">
        Mostrando <strong>{filtrados.length}</strong> de <strong>{(movimientos || []).length}</strong> movimiento(s)
      </div>
    </div>
  );
};

// ─── Modal de confirmacion al salir con cambios sin guardar ──────────────────
const UnsavedChangesModal = ({ open, theme, saving, onSave, onCancel, onDiscard }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
        <div className="px-6 py-4 border-b flex items-center gap-3" style={{ backgroundColor: theme.primaryLight }}>
          <AlertTriangle size={22} style={{ color: theme.primaryDark }} />
          <h3 className="font-bold text-gray-800 text-base">Cambios sin guardar</h3>
        </div>
        <div className="px-6 py-5 text-sm text-gray-700">
          ¿Seguro que quieres salir sin guardar los cambios?
        </div>
        <div className="px-6 pb-5 flex items-center justify-end gap-2 flex-wrap">
          <button
            onClick={onCancel}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
          >
            Cancelar
          </button>
          {onDiscard && (
            <button
              onClick={onDiscard}
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-red-600 bg-white border border-red-200 rounded hover:bg-red-50 disabled:opacity-50"
            >
              Salir sin guardar
            </button>
          )}
          <button
            onClick={onSave}
            disabled={saving}
            className="px-4 py-2 text-sm font-semibold text-white rounded shadow flex items-center gap-2 disabled:opacity-50"
            style={{ backgroundColor: theme.primary }}
          >
            {saving ? <Loader size={14} className="animate-spin" /> : <Save size={14} />}
            Guardar y salir
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Pagina principal ─────────────────────────────────────────────────────────
export default function GestionStock() {
  const { user } = useContext(AuthContext);
  const theme = useSafeTheme();
  const navigate = useNavigate();
  const isAdmin = user?.permisos === 1;
  const isClient = user?.permisos === 5;

  const [clientesDisponibles, setClientesDisponibles] = useState([]);
  const [clienteSel, setClienteSel] = useState(null);
  const [productos, setProductos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [clienteBusqueda, setClienteBusqueda] = useState('');

  const [busqueda, setBusqueda] = useState('');
  const [proveedorFiltro, setProveedorFiltro] = useState('');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 20;

  // Vista del listado de stock: 'unidades' (por defecto) | 'cajas'.
  //
  // PREMISA, y es la que hay que tener clara: `stock.cantidad`, `stock.minimo` y
  // `stock.maximo` están guardados EN UNIDADES SUELTAS. Los mínimos y máximos
  // que entregó el cliente en Excel vienen en unidades y se cargaron tal cual.
  // Por eso:
  //   'unidades' → el valor de BD TAL CUAL. No se convierte nada.
  //   'cajas'    → se DIVIDE por `factor_unidad_base` de cada fila.
  //
  // NUNCA se multiplica, en ningún modo. Multiplicar era el error que tenía
  // esta pantalla: un mínimo de 100 unidades se pintaba como 10.000.
  //
  // Es puramente de PRESENTACIÓN: se aplica en el render y no toca `cambios`
  // ni lo que se envía al guardar. Ver `mostrarValor` más abajo.
  const [vistaStock, setVistaStock] = useState('unidades');
  const enCajas = vistaStock === 'cajas';

  const [cambios, setCambios] = useState({});
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const [excelOpen, setExcelOpen] = useState(false);
  const [excelLotesOpen, setExcelLotesOpen] = useState(false);
  // Movimientos: cargados al entrar al cliente, filtrados client-side en el panel
  const [movimientos, setMovimientos] = useState([]);
  const [loadingMovimientos, setLoadingMovimientos] = useState(false);
  const [filtroMovProducto, setFiltroMovProducto] = useState(''); // pre-filtro al venir de "ver historial" en una fila
  const [pestaña, setPestaña] = useState('stock'); // 'stock' | 'pendientes' | 'lotes' | 'albaranes-mixto' | 'movimientos'
  const [pendientes, setPendientes] = useState([]);
  const [pendienteSel, setPendienteSel] = useState(null); // para modal de aceptar

  // Modo 'mixto': lista de albaranes pendientes de aceptación + modal para revisar uno
  const [albaranesMixto, setAlbaranesMixto] = useState([]);
  const [albaranAceptarModal, setAlbaranAceptarModal] = useState({ open: false, pedidoId: null });

  // Picking — lotes
  const [lotes, setLotes] = useState([]);
  const [lotesProductoModal, setLotesProductoModal] = useState({ open: false, producto: null });
  const [loteFormModal, setLoteFormModal] = useState({ open: false, lote: null, prefillProductoId: null });

  // Guard: cambios sin guardar al salir / cambiar de cliente / cambiar de pestaña
  const [navConfirmOpen, setNavConfirmOpen] = useState(false);
  const pendingNavRef = useRef(null);

  // ¿El usuario es matriz? (cliente cuyo cliente.clinica_matriz = 0, es decir,
  // el suyo NO depende de otro). Determinamos por el cliente al que pertenece.
  const [esMatriz, setEsMatriz] = useState(false);

  // El sidebar de clientes solo se muestra para superadmin o para cliente-matriz.
  // Para cliente hija, solo se ve la suya y sin selector.
  const mostrarSidebar = isAdmin || (isClient && esMatriz && clientesDisponibles.length > 1);

  // Carga inicial: clientes disponibles para el user
  useEffect(() => {
    fetch(`${API_URL}/api/stock/clientes-disponibles`, { headers: authHeaders() })
      .then(r => r.json())
      .then(d => {
        const list = d.clientes || [];
        setClientesDisponibles(list);
        // Determinar si el user es matriz: alguno de los clientes que ve no es el suyo directo.
        // Para isClient: si ve mas de un cliente, asumimos que es matriz con hijas.
        if (isClient && list.length > 1) setEsMatriz(true);
        // Si solo hay uno, autoseleccionarlo
        if (list.length === 1) setClienteSel(list[0]);
      });
  }, [isClient]);

  // Al cambiar de cliente, cargar su stock + proveedores asignados
  const [proveedoresCliente, setProveedoresCliente] = useState([]);

  const cargarStock = useCallback(() => {
    if (!clienteSel) return;
    setLoading(true);
    setCambios({});
    fetch(`${API_URL}/api/stock/cliente/${clienteSel.id}`, { headers: authHeaders() })
      .then(r => r.json())
      .then(d => setProductos(d.productos || []))
      .catch(e => console.error(e))
      .finally(() => setLoading(false));
  }, [clienteSel]);

  const cargarProveedoresCliente = useCallback(() => {
    if (!clienteSel) { setProveedoresCliente([]); return; }
    fetch(`${API_URL}/api/clientes/${clienteSel.id}/proveedores`, { headers: authHeaders() })
      .then(r => r.json())
      .then(d => setProveedoresCliente(d.proveedores || []))
      .catch(e => { console.error(e); setProveedoresCliente([]); });
  }, [clienteSel]);

  const cargarPendientes = useCallback(() => {
    if (!clienteSel) { setPendientes([]); return; }
    fetch(`${API_URL}/api/stock/cliente/${clienteSel.id}/pendientes-verificacion`, { headers: authHeaders() })
      .then(r => r.json())
      .then(d => setPendientes(d.pendientes || []))
      .catch(e => { console.error(e); setPendientes([]); });
  }, [clienteSel]);

  // Albaranes pendientes del modo 'mixto'. Cabecera + nº de líneas; el detalle
  // se carga al abrir el modal.
  const cargarAlbaranesMixto = useCallback(() => {
    if (!clienteSel) { setAlbaranesMixto([]); return; }
    fetch(`${API_URL}/api/stock/cliente/${clienteSel.id}/albaranes-pendientes`, { headers: authHeaders() })
      .then(r => r.json())
      .then(d => setAlbaranesMixto(d.albaranes || []))
      .catch(e => { console.error(e); setAlbaranesMixto([]); });
  }, [clienteSel]);

  const cargarLotes = useCallback(() => {
    if (!clienteSel) { setLotes([]); return; }
    fetch(`${API_URL}/api/stock/cliente/${clienteSel.id}/lotes`, { headers: authHeaders() })
      .then(r => r.json())
      .then(d => setLotes(d.lotes || []))
      .catch(e => { console.error(e); setLotes([]); });
  }, [clienteSel]);

  const cargarMovimientos = useCallback(() => {
    if (!clienteSel) { setMovimientos([]); return; }
    setLoadingMovimientos(true);
    fetch(`${API_URL}/api/stock/cliente/${clienteSel.id}/movimientos?limit=500`, { headers: authHeaders() })
      .then(r => r.json())
      .then(d => setMovimientos(d.movimientos || []))
      .catch(e => { console.error(e); setMovimientos([]); })
      .finally(() => setLoadingMovimientos(false));
  }, [clienteSel]);

  // Modo de stock del cliente seleccionado (decide qué pestañas mostrar)
  const modoStockCliente = clienteSel?.gestion_stock || 'inactivo';

  useEffect(() => { cargarStock(); }, [cargarStock]);
  useEffect(() => { cargarProveedoresCliente(); }, [cargarProveedoresCliente]);
  useEffect(() => {
    // Solo cargar pendientes si el cliente está en modo albarán
    if (modoStockCliente === 'albaran') cargarPendientes();
    else setPendientes([]);
  }, [cargarPendientes, modoStockCliente]);
  useEffect(() => {
    // Albaranes pendientes con lote/vencimiento aplican a modos 'albaran' y
    // 'mixto'. Único diff: 'albaran' es solo web, 'mixto' web + app.
    if (modoStockCliente === 'albaran' || modoStockCliente === 'mixto') cargarAlbaranesMixto();
    else setAlbaranesMixto([]);
  }, [cargarAlbaranesMixto, modoStockCliente]);
  useEffect(() => {
    // Lotes aplican a modos con trazabilidad: 'picking', 'albaran', 'mixto'.
    // En 'albaran' y 'mixto' los lotes se crean al aceptar el albarán pendiente
    // (lote obligatorio); en 'picking' al escanear desde la app.
    if (modoStockCliente !== 'inactivo' && modoStockCliente) cargarLotes();
    else setLotes([]);
  }, [cargarLotes, modoStockCliente]);
  useEffect(() => { cargarMovimientos(); }, [cargarMovimientos]);
  useEffect(() => { setPage(1); }, [clienteSel, busqueda, proveedorFiltro]);
  // Resetear pestaña a 'stock' al cambiar de cliente (las otras pestañas pueden no aplicar)
  useEffect(() => { setPestaña('stock'); }, [clienteSel?.id]);

  // Lista de proveedores para el filtro: los asignados al cliente (no derivados de productos)
  const proveedoresUnicos = useMemo(() => {
    return (proveedoresCliente || [])
      .slice()
      .sort((a, b) => String(a.nombre || '').localeCompare(String(b.nombre || '')));
  }, [proveedoresCliente]);

  // Filtros locales
  const clientesFiltrados = useMemo(() => {
    const t = clienteBusqueda.trim().toUpperCase();
    if (!t) return clientesDisponibles;
    return clientesDisponibles.filter(c =>
      (c.razon_social || '').toUpperCase().includes(t) ||
      (c.alias || '').toUpperCase().includes(t) ||
      (c.localidad || '').toUpperCase().includes(t)
    );
  }, [clientesDisponibles, clienteBusqueda]);

  const productosFiltrados = useMemo(() => {
    const t = busqueda.trim().toUpperCase();
    let filtered = productos;
    if (proveedorFiltro) {
      const pid = parseInt(proveedorFiltro);
      filtered = filtered.filter(p => p.proveedor_id === pid);
    }
    if (t) {
      filtered = filtered.filter(p =>
        (p.nombre || '').toUpperCase().includes(t) ||
        (p.ref_proveedor || '').toUpperCase().includes(t) ||
        (p.marca || '').toUpperCase().includes(t)
      );
    }

    // Orden en DOS ejes.
    //
    // Eje 1 — NIVEL DE GESTIÓN (ver `nivelGestion`): primero los productos con
    // mínimo Y máximo, después los que tienen algo puesto a mano (cantidad,
    // mínimo o máximo) y al final los que están vacíos del todo. Antes no era
    // así: un producto bien configurado y dentro de rango caía en el mismo
    // grupo "OK" que uno sin tocar, y quedaban mezclados. El listado ordena
    // por cuánto ha trabajado el cliente cada ficha, que es lo que le importa
    // al que gestiona el almacén.
    //
    // Eje 2 — dentro de cada bloque, el orden operativo de la app móvil:
    //   1) Bajo   (0 < cantidad < mínimo)
    //   2) Sobre  (cantidad > máximo)
    //   3) OK     (cantidad > 0 dentro de rango)
    //   4) Vacío  (cantidad === 0) — al final, productos del catálogo sin uso
    // Dentro de cada grupo: cantidad ASC, después nombre alfabético.
    // Importante: usamos `valorActual` para reflejar también los cambios en
    // edición sin guardar — si el user bajó la cantidad a 0 en un input,
    // el producto cae a la última posición de inmediato. valorActual está
    // disponible más abajo así que duplicamos la lógica inline acá.
    const valorEnEdicion = (p, campo) => {
      const c = cambios[p.producto_id];
      if (c && c[campo] !== undefined) return c[campo];
      return p[campo];
    };
    // Nivel de gestión del producto. Ordena por "cuánto ha tocado el cliente
    // esta ficha", de más a menos:
    //
    //   0 · COMPLETO — mínimo Y máximo. Es el único estado en el que la ficha
    //       avisa de las dos cosas (reposición y sobrestock).
    //   1 · A MEDIAS — hay algo puesto a mano (cantidad, mínimo o máximo) pero
    //       no los dos umbrales. Producto en uso, todavía sin terminar de
    //       configurar.
    //   2 · VACÍO    — ni cantidad ni umbrales. Catálogo sin estrenar, ruido
    //       para el que gestiona el almacén.
    const nivelGestion = (p) => {
      const cant = parseInt(valorEnEdicion(p, 'cantidad')) || 0;
      const min  = parseInt(valorEnEdicion(p, 'minimo'))   || 0;
      const max  = parseInt(valorEnEdicion(p, 'maximo'))   || 0;
      if (min > 0 && max > 0) return 0;
      if (cant > 0 || min > 0 || max > 0) return 1;
      return 2;
    };
    const prioridad = (p) => {
      const cant = parseInt(valorEnEdicion(p, 'cantidad')) || 0;
      const min  = parseInt(valorEnEdicion(p, 'minimo'))   || 0;
      const max  = parseInt(valorEnEdicion(p, 'maximo'))   || 0;
      if (cant === 0) return 3;
      if (min > 0 && cant < min) return 0;
      if (max > 0 && cant > max) return 1;
      return 2;
    };
    return [...filtered].sort((a, b) => {
      // Primero el nivel de gestión: completos, a medias, vacíos.
      const na = nivelGestion(a), nb = nivelGestion(b);
      if (na !== nb) return na - nb;
      const pa = prioridad(a), pb = prioridad(b);
      if (pa !== pb) return pa - pb;
      const ca = parseInt(valorEnEdicion(a, 'cantidad')) || 0;
      const cb = parseInt(valorEnEdicion(b, 'cantidad')) || 0;
      if (ca !== cb) return ca - cb;
      return String(a.nombre || '').localeCompare(String(b.nombre || ''));
    });
  }, [productos, busqueda, proveedorFiltro, cambios]);

  const totalPages = Math.max(1, Math.ceil(productosFiltrados.length / PAGE_SIZE));
  const pageStart = (page - 1) * PAGE_SIZE;
  const pageItems = productosFiltrados.slice(pageStart, pageStart + PAGE_SIZE);

  // Valor REAL del campo, SIEMPRE en unidades sueltas: el de la BD o el que el
  // usuario tenga en edición sin guardar. Es la única fuente de verdad — lo que
  // se guarda, con lo que se calcula el estado (bajo mínimo / sobrestock) y con
  // lo que se ordena la tabla. La vista en cajas NO pasa por aquí.
  const valorActual = (p, campo) => {
    const c = cambios[p.producto_id];
    if (c && c[campo] !== undefined) return c[campo];
    return p[campo];
  };

  // Factor de embalaje de una fila, normalizado. Lo calcula el backend
  // (`factor_unidad_base`, vía `factorEnvase`: columna explícita
  // `unidades_por_envase` y, si no, el texto libre `unidadbasica`). Un factor de
  // 1 significa "no sabemos convertir", NO "viene de una en una".
  const factorDe = (p) => {
    const f = Number(p.factor_unidad_base);
    return (!isFinite(f) || f < 1) ? 1 : f;
  };

  // Unidades sueltas → cajas. DIVIDE, truncando a 2 decimales hacia abajo.
  // Espejo exacto de `convertirACajas` en el backend
  // (routes/utils/unidades.js), donde está la justificación completa. Resumen:
  // 7 unidades de una caja de 100 son 0,07 cajas — redondear a 0 escondería 7
  // unidades y redondear a 1 se inventaría 93. Y se trunca en vez de redondear
  // para no crear existencias por arriba jamás.
  const aCajas = (valorEnUnidades, factor) => {
    const c = Number(valorEnUnidades);
    if (!isFinite(c)) return 0;
    if (!isFinite(factor) || factor < 1) return c;
    return Math.floor((c / factor) * 100) / 100;
  };

  // Lo que se PINTA en el input.
  //
  // EL SELECTOR Unidades/Cajas AFECTA ÚNICAMENTE A `cantidad`.
  //
  // `cantidad` es una magnitud de inventario: la misma existencia se puede
  // expresar en piezas o en cajas, así que tiene sentido verla de las dos
  // formas. `minimo` y `maximo` NO son eso: son umbrales de reposición que el
  // cliente define EN UNIDADES en su Excel. Son configuración, no una medida
  // que cambie de unidad al mirarla. Dividirlos convertía un mínimo de 30 en
  // "0,3" y dejaba de ser el número que el cliente configuró.
  //
  // Por eso `minimo` y `maximo` devuelven SIEMPRE `valorActual`, en los dos
  // modos, sin pasar por `aCajas`.
  const mostrarValor = (p, campo) => {
    const real = valorActual(p, campo);
    if (!enCajas || campo !== 'cantidad') return real;
    return aCajas(real, factorDe(p));
  };

  // Tooltip de la CANTIDAD convertida a cajas: deja siempre a la vista la cifra
  // REAL en unidades, porque el truncado a 2 decimales puede perder un resto
  // pequeño (1 unidad de una caja de 300 se ve como 0).
  // Solo aplica a `cantidad`: es el único campo que se convierte.
  const tooltipCajas = (p) => {
    const factor = factorDe(p);
    const real = Number(valorActual(p, 'cantidad')) || 0;
    if (factor <= 1) {
      return `Sin equivalencia de envase definida: no se puede convertir a cajas, `
        + `se muestran las ${real} unidades tal cual.`;
    }
    const cajas = aCajas(real, factor);
    return `${cajas} cajas · 1 ${p.unidadbasica || 'envase'} = ${factor} uds · ${real} unidades`;
  };

  // Tooltip de los umbrales. No habla de cajas jamás: van en unidades siempre.
  const tooltipUmbral = (campo) =>
    `${campo === 'minimo' ? 'Mínimo' : 'Máximo'} configurado para este cliente, `
    + `EN UNIDADES. Los umbrales de reposición no cambian con la vista: se muestran `
    + `igual en Unidades y en Cajas.`;

  // Cambio de vista. Se puede alternar SIEMPRE, también con ediciones sin
  // guardar.
  //
  // Antes esto se bloqueaba si había cambios pendientes, porque la vista en
  // cajas era de solo lectura y dejar al usuario mirando sus ediciones
  // divididas y sin poder tocarlas era una trampa. Ya no: la Cantidad se edita
  // en los dos modos y `cambios` vive en unidades, así que alternar de vista
  // reconvierte lo que haya pendiente y lo enseña correctamente.
  //
  // Además, poder alternar con cambios sin guardar es justo lo que se pide:
  // teclear 3 cajas, pasar a unidades y ver 300 — la comprobación de que lo que
  // se va a guardar es lo que uno cree.
  const cambiarVista = (modo) => setVistaStock(modo);

  // Productos DEL LISTADO de gestión por unidades. Sus `minimo` y `maximo` los
  // fija el cliente en su Excel y no se editan desde esta pantalla nunca.
  //
  // Se usa `gestion_por_unidades` y NO `mostrar_en_unidades` a propósito: un
  // producto puede estar en el listado sin tener factor de envase conocido
  // (`unidades_por_envase` a NULL y `unidadbasica` sin parsear). Sus mínimos
  // vienen del Excel igual que los demás y también hay que bloquearlos.
  // `mostrar_en_unidades` exige además factor > 1 — sirve para el distintivo
  // visual, no para decidir permisos de edición.
  const productosDelListado = useMemo(
    () => new Set(
      (productos || [])
        .filter(p => Number(p.gestion_por_unidades) === 1)
        .map(p => p.producto_id)
    ),
    [productos]
  );

  // Entrada de datos. `cambios` guarda SIEMPRE unidades sueltas, pase lo que
  // pase: es lo que viaja en el PUT /bulk y lo que `stock` almacena. Si el
  // usuario está en modo cajas, aquí se convierte a unidades ANTES de guardar.
  //
  // Esta función es la INVERSA de `mostrarValor`: una divide al pintar y la otra
  // multiplica al escribir. Manteniendo `cambios` en una sola unidad, la
  // sincronización entre las dos vistas sale gratis — el valor tecleado en cajas
  // se guarda en unidades y, al volver a cajas, `mostrarValor` lo vuelve a
  // dividir. NO hay estado duplicado ni sincronización manual, y no debe
  // haberla: sería la típica fuente de bugs de "los dos números no cuadran".
  //
  // `factor` lo pasa la fila desde `MainPanel` (`factorDe(p)`), porque el factor
  // es de cada producto y aquí no tenemos la fila.
  const onCellChange = (productoId, campo, value, factor = 1) => {
    // Cortafuegos — `minimo` y `maximo` no se editan en los productos del
    // listado, los fija el Excel del cliente. Fuera del listado se editan con
    // normalidad EN LOS DOS MODOS: como no se convierten, la vista de cajas no
    // aporta ningún riesgo sobre ellos.
    if ((campo === 'minimo' || campo === 'maximo')
        && productosDelListado.has(productoId)) return;

    // Solo `cantidad` se convierte, y solo en modo cajas. Los umbrales se
    // teclean y se guardan en unidades siempre.
    const convierte = enCajas && campo === 'cantidad';

    let num;
    if (value === '') {
      // Campo vacío: se mantiene el comportamiento de siempre (0). No pasa por
      // la conversión, para no acabar en un NaN * factor.
      num = 0;
    } else if (convierte) {
      // Cajas -> unidades. `parseFloat` y no `parseInt`: 0,07 cajas es un valor
      // legítimo y `parseInt` lo truncaría a 0. Se acepta la coma como separador
      // decimal por si el navegador la deja pasar.
      const cajas = parseFloat(String(value).replace(',', '.'));
      // Math.round y NO Math.floor: `stock.cantidad` es entero, así que hay que
      // caer al entero de unidad MÁS CERCANO. 2,5 cajas de 100 son 250 unidades
      // exactas; 0,333 cajas de 100 son 33,3 y se guardan como 33. Truncar
      // sesgaría el inventario siempre a la baja.
      num = isFinite(cajas) ? Math.round(cajas * factor) : 0;
    } else {
      num = parseInt(value, 10) || 0;
    }

    num = Math.max(0, num);
    setCambios(prev => ({
      ...prev,
      [productoId]: { ...(prev[productoId] || {}), [campo]: num },
    }));
  };

  const hayCambios = Object.keys(cambios).length > 0;

  const guardarCambios = useCallback(async () => {
    if (!hayCambios || !clienteSel) return false;
    setSaving(true);
    try {
      const payload = {
        cambios: Object.entries(cambios).map(([producto_id, c]) => ({
          producto_id: parseInt(producto_id),
          ...c,
        })),
      };
      const r = await fetch(`${API_URL}/api/stock/cliente/${clienteSel.id}/bulk`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(payload),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.message || 'Error al guardar');
      setToast({ type: 'success', msg: `${data.movimientos_registrados} cambios guardados` });
      setCambios({});
      cargarStock();
      return true;
    } catch (e) {
      setToast({ type: 'error', msg: e.message });
      return false;
    } finally {
      setSaving(false);
      setTimeout(() => setToast(null), 5000);
    }
  }, [hayCambios, clienteSel, cambios, cargarStock]);

  // Intermedia: cualquier accion que vaya a "perder" cambios debe pasar por aqui.
  const requestNavigation = useCallback((action) => {
    if (!hayCambios) { action(); return; }
    pendingNavRef.current = action;
    setNavConfirmOpen(true);
  }, [hayCambios]);

  const handleNavCancel = useCallback(() => {
    pendingNavRef.current = null;
    setNavConfirmOpen(false);
  }, []);

  const handleNavDiscard = useCallback(() => {
    const action = pendingNavRef.current;
    pendingNavRef.current = null;
    setNavConfirmOpen(false);
    setCambios({});
    if (typeof action === 'function') action();
  }, []);

  const handleNavSaveAndGo = useCallback(async () => {
    const ok = await guardarCambios();
    if (!ok) return; // si falla el guardado, dejamos al user en la pagina
    const action = pendingNavRef.current;
    pendingNavRef.current = null;
    setNavConfirmOpen(false);
    if (typeof action === 'function') action();
  }, [guardarCambios]);

  // Eliminar un lote (solo si cantidad = 0; el backend lo enforce)
  const handleEliminarLote = useCallback(async (lote) => {
    if (lote.cantidad > 0) {
      setToast({ type: 'error', msg: 'No se puede eliminar un lote con cantidad > 0. Realiza una salida primero.' });
      setTimeout(() => setToast(null), 5000);
      return;
    }
    if (!window.confirm(`¿Eliminar el lote ${lote.lote}?`)) return;
    try {
      const r = await fetch(`${API_URL}/api/stock/lotes/${lote.id}`, {
        method: 'DELETE', headers: authHeaders(),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.message || 'Error');
      setToast({ type: 'success', msg: 'Lote eliminado' });
      setTimeout(() => setToast(null), 3000);
      cargarLotes();
    } catch (e) {
      setToast({ type: 'error', msg: e.message });
      setTimeout(() => setToast(null), 5000);
    }
  }, [cargarLotes]);

  // Guard nativo (cierre / refresh / atras del navegador) + intercept de Links de la SPA
  useEffect(() => {
    if (!hayCambios) return undefined;

    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
      return '';
    };

    const handleDocumentClick = (e) => {
      // Solo botones izquierdos sin modificador
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const anchor = e.target.closest && e.target.closest('a[href]');
      if (!anchor) return;
      const href = anchor.getAttribute('href');
      if (!href) return;
      // Ignorar externos, anclas y mailto/tel
      if (/^(https?:)?\/\//.test(href) || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) return;
      // Ignorar target=_blank o download
      if (anchor.target === '_blank' || anchor.hasAttribute('download')) return;
      // Si va al mismo lugar, dejar pasar
      if (href === window.location.pathname + window.location.search) return;
      e.preventDefault();
      e.stopPropagation();
      pendingNavRef.current = () => navigate(href);
      setNavConfirmOpen(true);
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('click', handleDocumentClick, true);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('click', handleDocumentClick, true);
    };
  }, [hayCambios, navigate]);

  // ── Acceso restringido ───────────────────────────────────────────────────
  if (!isAdmin && !isClient) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center bg-gray-50 p-6">
        <div className="bg-white rounded-2xl shadow p-10 text-center max-w-md">
          <AlertTriangle size={40} className="mx-auto text-amber-500 mb-4" />
          <h2 className="text-xl font-bold text-gray-800 mb-2">Acceso restringido</h2>
          <p className="text-gray-600 text-sm">Esta sección es solo para administradores y clínicas con gestión de stock.</p>
        </div>
      </div>
    );
  }

  // ── Layout sin sidebar (cliente hija o cliente unico) ────────────────────
  if (!mostrarSidebar) {
    return (
      <div className="fixed inset-x-0 top-16 bottom-0 bg-gray-50 overflow-hidden">
        <MainPanel
          clienteSel={clienteSel}
          vistaStock={vistaStock}
          enCajas={enCajas}
          cambiarVista={cambiarVista}
          factorDe={factorDe}
          mostrarValor={mostrarValor}
          tooltipCajas={tooltipCajas}
          tooltipUmbral={tooltipUmbral}
          productos={productos}
          productosFiltrados={productosFiltrados}
          pageItems={pageItems}
          pageStart={pageStart}
          page={page}
          setPage={setPage}
          totalPages={totalPages}
          PAGE_SIZE={PAGE_SIZE}
          loading={loading}
          busqueda={busqueda}
          setBusqueda={setBusqueda}
          proveedorFiltro={proveedorFiltro}
          setProveedorFiltro={setProveedorFiltro}
          proveedoresUnicos={proveedoresUnicos}
          cambios={cambios}
          valorActual={valorActual}
          onCellChange={onCellChange}
          hayCambios={hayCambios}
          guardarCambios={guardarCambios}
          saving={saving}
          theme={theme}
          onVerMovimientosProducto={(p) => { setFiltroMovProducto(p.nombre || ''); setPestaña('movimientos'); }}
          onExcelOpen={() => setExcelOpen(true)}
          onExcelLotesOpen={() => setExcelLotesOpen(true)}
          movimientos={movimientos}
          loadingMovimientos={loadingMovimientos}
          filtroMovProducto={filtroMovProducto}
          onClearFiltroMov={() => setFiltroMovProducto('')}
          pestaña={pestaña}
          setPestaña={(next) => requestNavigation(() => setPestaña(next))}
          pendientes={pendientes}
          onAceptarPendiente={(p) => setPendienteSel(p)}
          onRechazarPendiente={async (p) => {
            if (!window.confirm(`¿Rechazar el pendiente de ${p.producto_nombre}? No se sumará al stock.`)) return;
            await fetch(`${API_URL}/api/stock/pendiente-verificacion/${p.id}/rechazar`, {
              method: 'POST', headers: authHeaders()
            });
            cargarPendientes();
          }}
          modoStockCliente={modoStockCliente}
          lotes={lotes}
          onAbrirLotesProducto={(p) => setLotesProductoModal({ open: true, producto: p })}
          onNuevoLote={() => setLoteFormModal({ open: true, lote: null, prefillProductoId: null })}
          onEditarLote={(l) => setLoteFormModal({ open: true, lote: l, prefillProductoId: l.producto_id })}
          onEliminarLote={handleEliminarLote}
          albaranesMixto={albaranesMixto}
          onAbrirAlbaranMixto={(pedidoId) => setAlbaranAceptarModal({ open: true, pedidoId })}
        />

        <ExcelModal
          open={excelOpen}
          onClose={() => setExcelOpen(false)}
          cliente={clienteSel}
          theme={theme}
          onApplied={(data) => {
            setToast({ type: 'success', msg: `${data.movimientos_registrados} cambios importados desde Excel` });
            setTimeout(() => setToast(null), 6000);
            cargarStock();
          }}
        />

        <ExcelLotesModal
          open={excelLotesOpen}
          onClose={() => setExcelLotesOpen(false)}
          cliente={clienteSel}
          theme={theme}
          onApplied={() => {
            setToast({ type: 'success', msg: 'Lotes importados correctamente' });
            setTimeout(() => setToast(null), 5000);
            cargarLotes();
            cargarStock();
          }}
        />

        <AceptarPendienteModal
          pendiente={pendienteSel}
          theme={theme}
          onClose={() => setPendienteSel(null)}
          onApplied={() => {
            setPendienteSel(null);
            setToast({ type: 'success', msg: 'Cantidades sumadas al stock' });
            setTimeout(() => setToast(null), 5000);
            cargarPendientes();
            cargarStock();
          }}
        />

        {/* Modal de revisión de albarán mixto/albarán — también para cliente
            sin sidebar; sin esto el botón "Revisar" setea el estado pero no
            hay nada renderizado que lo muestre. */}
        <AlbaranMixtoAceptarModal
          open={albaranAceptarModal.open}
          pedidoId={albaranAceptarModal.pedidoId}
          onClose={() => setAlbaranAceptarModal({ open: false, pedidoId: null })}
          onResolved={() => {
            setAlbaranAceptarModal({ open: false, pedidoId: null });
            cargarAlbaranesMixto();
            cargarStock();
            cargarMovimientos();
          }}
        />

        {/* Modal: lotes de un producto (cliente picking sin sidebar) */}
        <LotesProductoModal
          open={lotesProductoModal.open}
          producto={lotesProductoModal.producto}
          clienteId={clienteSel?.id}
          theme={theme}
          onClose={() => setLotesProductoModal({ open: false, producto: null })}
          onNuevoLote={() => {
            setLoteFormModal({ open: true, lote: null, prefillProductoId: lotesProductoModal.producto?.producto_id || null });
          }}
          onEditarLote={(lote) => {
            setLoteFormModal({ open: true, lote, prefillProductoId: lote.producto_id });
          }}
          onApplied={() => { cargarLotes(); cargarStock(); }}
        />

        {/* Modal: form alta/edit de lote (cliente picking sin sidebar) */}
        <LoteFormModal
          open={loteFormModal.open}
          lote={loteFormModal.lote}
          prefillProductoId={loteFormModal.prefillProductoId}
          clienteId={clienteSel?.id}
          productos={productos}
          theme={theme}
          onClose={() => setLoteFormModal({ open: false, lote: null, prefillProductoId: null })}
          onSaved={() => {
            setLoteFormModal({ open: false, lote: null, prefillProductoId: null });
            cargarLotes();
            cargarStock();
          }}
        />

        <UnsavedChangesModal
          open={navConfirmOpen}
          theme={theme}
          saving={saving}
          onSave={handleNavSaveAndGo}
          onCancel={handleNavCancel}
          onDiscard={handleNavDiscard}
        />

        {toast && <Toast toast={toast} theme={theme} />}
      </div>
    );
  }

  // ── Layout con sidebar (admin o matriz) ──────────────────────────────────
  return (
    <div className="fixed inset-x-0 top-16 bottom-0 bg-gray-50 flex overflow-hidden">

      <aside className="w-80 bg-white border-r border-gray-200 flex flex-col">
        <div className="p-4 border-b" style={{ backgroundColor: theme.primaryLight }}>
          <h2 className="font-bold text-gray-800 flex items-center gap-2">
            <Building2 size={18} style={{ color: theme.primary }} />
            Clínicas con stock
          </h2>
          <p className="text-xs text-gray-600 mt-1">
            {isAdmin ? 'Todas las clínicas con gestión activada' : 'Tu matriz y sus clínicas hijas'}
          </p>
        </div>

        <div className="p-3 border-b">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search size={16} className="text-gray-400" />
            </div>
            <input
              type="text"
              value={clienteBusqueda}
              onChange={(e) => setClienteBusqueda(e.target.value)}
              placeholder="Buscar clínica..."
              className="w-full pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none transition-colors"
              style={{ paddingLeft: '40px' }}
              onFocus={(e) => e.target.style.borderColor = theme.primary}
              onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {clientesFiltrados.length === 0 ? (
            <div className="p-8 text-center text-gray-400 text-sm">
              {clienteBusqueda ? 'Sin coincidencias' : 'Sin clínicas disponibles'}
            </div>
          ) : (
            clientesFiltrados.map(c => {
              const selected = clienteSel?.id === c.id;
              return (
                <button
                  key={c.id}
                  onClick={() => requestNavigation(() => setClienteSel(c))}
                  className="w-full text-left p-3 border-b transition-colors"
                  style={selected
                    ? { backgroundColor: theme.primaryLight, borderLeft: `4px solid ${theme.primary}` }
                    : {}}
                  onMouseEnter={(e) => !selected && (e.currentTarget.style.backgroundColor = theme.primaryLight)}
                  onMouseLeave={(e) => !selected && (e.currentTarget.style.backgroundColor = '')}
                >
                  <div className="font-semibold text-gray-800 text-sm truncate">{c.razon_social}</div>
                  <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-2">
                    <span>{c.localidad || '—'}</span>
                    {c.clinica_matriz === 0 && (
                      <>
                        <span>·</span>
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-medium"
                          style={{ backgroundColor: theme.primaryLight, color: theme.primaryDark }}>
                          <Users size={9} /> Matriz
                        </span>
                      </>
                    )}
                  </div>
                </button>
              );
            })
          )}
        </div>
      </aside>

      <main className="flex-1 overflow-hidden">
        {!clienteSel ? (
          <div className="h-full flex items-center justify-center text-center p-10">
            <div>
              <Boxes size={48} className="mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-semibold text-gray-700">Selecciona una clínica</h3>
              <p className="text-sm text-gray-500 mt-2 max-w-sm">
                Elige una clínica en el panel izquierdo para gestionar su inventario.
              </p>
            </div>
          </div>
        ) : (
          <MainPanel
            clienteSel={clienteSel}
            vistaStock={vistaStock}
            enCajas={enCajas}
            cambiarVista={cambiarVista}
            factorDe={factorDe}
            mostrarValor={mostrarValor}
            tooltipCajas={tooltipCajas}
            tooltipUmbral={tooltipUmbral}
            productos={productos}
            productosFiltrados={productosFiltrados}
            pageItems={pageItems}
            pageStart={pageStart}
            page={page}
            setPage={setPage}
            totalPages={totalPages}
            PAGE_SIZE={PAGE_SIZE}
            loading={loading}
            busqueda={busqueda}
            setBusqueda={setBusqueda}
            proveedorFiltro={proveedorFiltro}
            setProveedorFiltro={setProveedorFiltro}
            proveedoresUnicos={proveedoresUnicos}
            cambios={cambios}
            valorActual={valorActual}
            onCellChange={onCellChange}
            hayCambios={hayCambios}
            guardarCambios={guardarCambios}
            saving={saving}
            theme={theme}
            onVerMovimientosProducto={(p) => { setFiltroMovProducto(p.nombre || ''); setPestaña('movimientos'); }}
            onExcelOpen={() => setExcelOpen(true)}
            onExcelLotesOpen={() => setExcelLotesOpen(true)}
            movimientos={movimientos}
            loadingMovimientos={loadingMovimientos}
            filtroMovProducto={filtroMovProducto}
            onClearFiltroMov={() => setFiltroMovProducto('')}
              pestaña={pestaña}
            setPestaña={(next) => requestNavigation(() => setPestaña(next))}
            pendientes={pendientes}
            onAceptarPendiente={(p) => setPendienteSel(p)}
            onRechazarPendiente={async (p) => {
              if (!window.confirm(`¿Rechazar el pendiente de ${p.producto_nombre}? No se sumará al stock.`)) return;
              await fetch(`${API_URL}/api/stock/pendiente-verificacion/${p.id}/rechazar`, {
                method: 'POST', headers: authHeaders()
              });
              cargarPendientes();
            }}
            modoStockCliente={modoStockCliente}
            lotes={lotes}
            onAbrirLotesProducto={(p) => setLotesProductoModal({ open: true, producto: p })}
            onNuevoLote={() => setLoteFormModal({ open: true, lote: null, prefillProductoId: null })}
            onEditarLote={(l) => setLoteFormModal({ open: true, lote: l, prefillProductoId: l.producto_id })}
            onEliminarLote={handleEliminarLote}
            albaranesMixto={albaranesMixto}
            onAbrirAlbaranMixto={(pedidoId) => setAlbaranAceptarModal({ open: true, pedidoId })}
          />
        )}
      </main>

      <AlbaranMixtoAceptarModal
        open={albaranAceptarModal.open}
        pedidoId={albaranAceptarModal.pedidoId}
        onClose={() => setAlbaranAceptarModal({ open: false, pedidoId: null })}
        onResolved={() => {
          setAlbaranAceptarModal({ open: false, pedidoId: null });
          cargarAlbaranesMixto();
          cargarStock();
          cargarMovimientos();
        }}
      />

      <ExcelModal
        open={excelOpen}
        onClose={() => setExcelOpen(false)}
        cliente={clienteSel}
        theme={theme}
        onApplied={(data) => {
          setToast({ type: 'success', msg: `${data.movimientos_registrados} cambios importados desde Excel` });
          setTimeout(() => setToast(null), 6000);
          cargarStock();
        }}
      />

      <ExcelLotesModal
        open={excelLotesOpen}
        onClose={() => setExcelLotesOpen(false)}
        cliente={clienteSel}
        theme={theme}
        onApplied={() => {
          setToast({ type: 'success', msg: 'Lotes importados correctamente' });
          setTimeout(() => setToast(null), 5000);
          cargarLotes();
          cargarStock();
        }}
      />

      <UnsavedChangesModal
        open={navConfirmOpen}
        theme={theme}
        saving={saving}
        onSave={handleNavSaveAndGo}
        onCancel={handleNavCancel}
        onDiscard={handleNavDiscard}
      />

      {toast && <Toast toast={toast} theme={theme} />}

      {/* Modal: lotes de un producto (solo cliente picking) */}
      <LotesProductoModal
        open={lotesProductoModal.open}
        producto={lotesProductoModal.producto}
        clienteId={clienteSel?.id}
        theme={theme}
        onClose={() => setLotesProductoModal({ open: false, producto: null })}
        onNuevoLote={() => {
          setLoteFormModal({ open: true, lote: null, prefillProductoId: lotesProductoModal.producto?.producto_id || null });
        }}
        onEditarLote={(lote) => {
          setLoteFormModal({ open: true, lote, prefillProductoId: lote.producto_id });
        }}
        onApplied={() => { cargarLotes(); cargarStock(); }}
      />

      {/* Modal: form alta/edit de lote */}
      <LoteFormModal
        open={loteFormModal.open}
        lote={loteFormModal.lote}
        prefillProductoId={loteFormModal.prefillProductoId}
        clienteId={clienteSel?.id}
        productos={productos}
        theme={theme}
        onClose={() => setLoteFormModal({ open: false, lote: null, prefillProductoId: null })}
        onSaved={() => {
          setLoteFormModal({ open: false, lote: null, prefillProductoId: null });
          cargarLotes();
          cargarStock();
        }}
      />

      {/* Modal de aceptar pendiente de verificación — faltaba en el layout
          con sidebar, así el admin/matriz tampoco podía aceptar pendientes. */}
      <AceptarPendienteModal
        pendiente={pendienteSel}
        theme={theme}
        onClose={() => setPendienteSel(null)}
        onApplied={() => {
          setPendienteSel(null);
          setToast({ type: 'success', msg: 'Cantidades sumadas al stock' });
          setTimeout(() => setToast(null), 5000);
          cargarPendientes();
          cargarStock();
        }}
      />
    </div>
  );
}


// ─── Helpers para estado de lote según vencimiento ───────────────────────────
const DIAS_PROXIMO_CADUCAR = 30;

const estadoLote = (fechaVenc) => {
  if (!fechaVenc) return { key: 'sin_caducidad', label: 'Sin caducidad', color: 'bg-gray-100 text-gray-700' };
  const hoy = new Date(); hoy.setHours(0, 0, 0, 0);
  const venc = new Date(fechaVenc); venc.setHours(0, 0, 0, 0);
  const diasParaVencer = Math.round((venc - hoy) / (1000 * 60 * 60 * 24));
  if (diasParaVencer < 0) return { key: 'caducado', label: 'Caducado', color: 'bg-red-100 text-red-700' };
  if (diasParaVencer <= DIAS_PROXIMO_CADUCAR) return { key: 'proximo', label: `Vence en ${diasParaVencer}d`, color: 'bg-amber-100 text-amber-700' };
  return { key: 'vigente', label: 'Vigente', color: 'bg-emerald-100 text-emerald-700' };
};


// ─── Panel: Lotes (pestaña en modo picking) ──────────────────────────────────
const LotesPanel = ({ lotes, productos, theme, onNuevoLote, onEditarLote, onEliminarLote, onVerProducto }) => {
  const [filtroProducto, setFiltroProducto] = useState('');
  const [filtroEstado,  setFiltroEstado]  = useState('todos'); // todos|vigente|proximo|caducado|sin_caducidad

  const filtrados = useMemo(() => {
    let arr = lotes;
    if (filtroProducto) {
      const t = filtroProducto.trim().toUpperCase();
      arr = arr.filter(l =>
        (l.producto_nombre || '').toUpperCase().includes(t) ||
        (l.ref_proveedor   || '').toUpperCase().includes(t) ||
        (l.lote            || '').toUpperCase().includes(t)
      );
    }
    if (filtroEstado !== 'todos') {
      arr = arr.filter(l => estadoLote(l.fecha_vencimiento).key === filtroEstado);
    }
    return arr;
  }, [lotes, filtroProducto, filtroEstado]);

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Toolbar de filtros + acciones */}
      <div className="px-4 py-2.5 border-b bg-gray-50/50 flex flex-wrap gap-2 flex-shrink-0">
        <div className="relative flex-1 min-w-[220px]">
          <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none">
            <Search size={14} className="text-gray-400" />
          </div>
          <input
            type="text"
            value={filtroProducto}
            onChange={(e) => setFiltroProducto(e.target.value)}
            placeholder="Buscar producto, ref o lote..."
            className="w-full pr-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none"
            style={{ paddingLeft: '32px' }}
            onFocus={(e) => e.target.style.borderColor = theme.primary}
            onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
          />
        </div>
        <select
          value={filtroEstado}
          onChange={(e) => setFiltroEstado(e.target.value)}
          className="px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none bg-white"
        >
          <option value="todos">Todos los estados</option>
          <option value="vigente">Vigentes</option>
          <option value="proximo">Próximos a caducar ({DIAS_PROXIMO_CADUCAR}d)</option>
          <option value="caducado">Caducados</option>
          <option value="sin_caducidad">Sin caducidad</option>
        </select>
      </div>

      {/* Tabla scrolleable */}
      <div className="flex-1 overflow-auto">
        {filtrados.length === 0 ? (
          <div className="p-12 text-center text-gray-400 text-sm">
            {lotes.length === 0 ? 'Sin lotes registrados todavía.' : 'Sin coincidencias.'}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b sticky top-0">
              <tr>
                <th className="text-left px-3 py-2 font-semibold text-gray-700">Producto</th>
                <th className="text-left px-3 py-2 font-semibold text-gray-700">Ref.</th>
                <th className="text-left px-3 py-2 font-semibold text-gray-700">Lote</th>
                <th className="text-left px-3 py-2 font-semibold text-gray-700">Vencimiento</th>
                <th className="text-right px-3 py-2 font-semibold text-gray-700">Cantidad</th>
                <th className="text-left px-3 py-2 font-semibold text-gray-700">Estado</th>
                <th className="text-center px-3 py-2 w-28"></th>
              </tr>
            </thead>
            <tbody>
              {filtrados.map(l => {
                const est = estadoLote(l.fecha_vencimiento);
                return (
                  <tr key={l.id} className="border-t hover:bg-gray-50">
                    <td className="px-3 py-2 text-gray-800 font-medium truncate max-w-xs" title={l.producto_nombre}>
                      {l.producto_nombre}
                    </td>
                    <td className="px-3 py-2 text-gray-500 font-mono text-xs">{l.ref_proveedor || '—'}</td>
                    <td className="px-3 py-2 text-gray-700 font-mono">{l.lote}</td>
                    <td className="px-3 py-2 text-gray-600 text-xs">
                      {l.fecha_vencimiento
                        ? new Date(l.fecha_vencimiento).toLocaleDateString('es-ES')
                        : '—'}
                    </td>
                    <td className="px-3 py-2 text-right font-semibold">{l.cantidad}</td>
                    <td className="px-3 py-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${est.color}`}>
                        {est.label}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => onVerProducto(l)}
                          className="p-1 text-gray-400 hover:text-gray-700 rounded"
                          title="Ver lotes de este producto"
                        >
                          <Eye size={14} />
                        </button>
                        <button
                          onClick={() => onEditarLote(l)}
                          className="p-1 text-blue-500 hover:bg-blue-50 rounded"
                          title="Editar lote"
                        >
                          <FileEdit size={14} />
                        </button>
                        <button
                          onClick={() => onEliminarLote(l)}
                          disabled={l.cantidad > 0}
                          className="p-1 text-red-500 hover:bg-red-50 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                          title={l.cantidad > 0 ? 'Solo lotes con cantidad 0' : 'Eliminar lote'}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div className="border-t px-4 py-2 bg-gray-50 text-xs text-gray-600 flex-shrink-0">
        Mostrando <strong>{filtrados.length}</strong> de <strong>{lotes.length}</strong> lote(s)
      </div>
    </div>
  );
};


// ─── Modal: lotes de un producto específico ──────────────────────────────────
const LotesProductoModal = ({ open, producto, clienteId, theme, onClose, onApplied }) => {
  const [lotes, setLotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editingLote, setEditingLote] = useState(null);
  const [editForm, setEditForm] = useState({ cantidad: '', lote: '', fecha_vencimiento: '' });
  const [saving, setSaving] = useState(false);

  const cargar = useCallback(() => {
    if (!open || !producto || !clienteId) return;
    setLoading(true);
    setError('');
    fetch(`${API_URL}/api/stock/cliente/${clienteId}/lotes?producto_id=${producto.producto_id}`, {
      headers: authHeaders(),
    })
      .then(r => r.json())
      .then(d => setLotes(d.lotes || []))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [open, producto, clienteId]);

  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => { if (!open) { setEditingLote(null); setError(''); } }, [open]);
  useEffect(() => {
    if (editingLote) {
      setEditForm({
        cantidad: String(editingLote.cantidad ?? 0),
        lote: editingLote.lote || '',
        fecha_vencimiento: editingLote.fecha_vencimiento ? String(editingLote.fecha_vencimiento).slice(0, 10) : '',
      });
      setError('');
    }
  }, [editingLote]);

  if (!open || !producto) return null;

  const handleEliminar = async (lote) => {
    if (lote.cantidad > 0) {
      setError('No se puede eliminar un lote con cantidad > 0. Realiza una salida primero.');
      return;
    }
    if (!window.confirm(`¿Eliminar el lote ${lote.lote}?`)) return;
    try {
      const r = await fetch(`${API_URL}/api/stock/lotes/${lote.id}`, {
        method: 'DELETE', headers: authHeaders(),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.message || 'Error');
      cargar();
      if (onApplied) onApplied();
    } catch (e) {
      setError(e.message);
    }
  };

  const handleGuardarEdit = async () => {
    if (!editingLote) return;
    const qty = parseInt(editForm.cantidad);
    if (!Number.isFinite(qty) || qty < 0) { setError('Cantidad inválida'); return; }
    if (!editForm.lote.trim())             { setError('El número de lote no puede estar vacío'); return; }

    const fechaActual = editingLote.fecha_vencimiento ? String(editingLote.fecha_vencimiento).slice(0, 10) : '';
    const body = {};
    if (qty !== editingLote.cantidad) body.cantidad = qty;
    if (editForm.lote.trim() !== editingLote.lote) body.lote = editForm.lote.trim();
    if (editForm.fecha_vencimiento !== fechaActual) body.fecha_vencimiento = editForm.fecha_vencimiento || null;

    if (Object.keys(body).length === 0) {
      setEditingLote(null);
      return;
    }

    setSaving(true);
    setError('');
    try {
      const r = await fetch(`${API_URL}/api/stock/lotes/${editingLote.id}`, {
        method: 'PATCH', headers: authHeaders(), body: JSON.stringify(body),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.message || 'Error al guardar');
      setEditingLote(null);
      await new Promise(r2 => setTimeout(r2, 0));
      cargar();
      if (onApplied) onApplied();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  // Ancho dinámico: cuando se edita un lote, el modal se expande para mostrar
  // el form de edición a la derecha (mismo patrón que el panel "?" del Excel).
  const modalMaxWidth = editingLote ? 'max-w-5xl' : 'max-w-3xl';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className={`bg-white rounded-2xl shadow-2xl w-full ${modalMaxWidth} max-h-[90vh] overflow-hidden flex flex-col transition-[max-width] duration-200`}>
        <div className="flex items-center justify-between px-6 py-4 border-b" style={{ backgroundColor: theme.primaryLight }}>
          <div className="flex items-center gap-3">
            <Package size={20} style={{ color: theme.primary }} />
            <div>
              <h2 className="text-lg font-bold text-gray-800">Lotes del producto</h2>
              <p className="text-xs text-gray-600 max-w-md truncate">{producto.nombre}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white rounded-lg">
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        {error && (
          <div className="mx-6 mt-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700 flex items-start gap-2">
            <AlertCircle size={16} className="mt-0.5" /><span>{error}</span>
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-6">
          <div className="flex gap-4 items-start">
            {/* Izquierda: tabla de lotes */}
            <div className="flex-1 min-w-0">
              {loading ? (
                <div className="text-center text-gray-400 py-8"><Loader size={20} className="animate-spin mx-auto" /></div>
              ) : lotes.length === 0 ? (
                <div className="text-center text-gray-400 text-sm border-2 border-dashed border-gray-200 rounded-lg p-8">
                  Este producto no tiene lotes todavía.
                </div>
              ) : (
                <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
                  <thead className="bg-gray-50 text-xs text-gray-600">
                    <tr>
                      <th className="text-left px-3 py-2">Lote</th>
                      <th className="text-left px-3 py-2">Vencimiento</th>
                      <th className="text-right px-3 py-2">Cantidad</th>
                      <th className="text-left px-3 py-2">Estado</th>
                      <th className="px-3 py-2 w-20"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {lotes.map(l => {
                      const est = estadoLote(l.fecha_vencimiento);
                      const isEditing = editingLote?.id === l.id;
                      return (
                        <tr key={l.id} className={`border-t ${isEditing ? 'bg-blue-50' : 'hover:bg-gray-50'}`}>
                          <td className="px-3 py-2 font-mono text-gray-800">{l.lote}</td>
                          <td className="px-3 py-2 text-gray-600 text-xs">
                            {l.fecha_vencimiento
                              ? new Date(l.fecha_vencimiento).toLocaleDateString('es-ES')
                              : '—'}
                          </td>
                          <td className="px-3 py-2 text-right font-semibold">{l.cantidad}</td>
                          <td className="px-3 py-2">
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${est.color}`}>
                              {est.label}
                            </span>
                          </td>
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => setEditingLote(l)}
                                className={`p-1 rounded ${isEditing ? 'bg-blue-100 text-blue-700' : 'text-blue-500 hover:bg-blue-50'}`}
                                title="Editar"
                              ><FileEdit size={13} /></button>
                              <button
                                onClick={() => handleEliminar(l)}
                                disabled={l.cantidad > 0}
                                className="p-1 text-red-500 hover:bg-red-50 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                                title={l.cantidad > 0 ? 'Solo lotes con cantidad 0' : 'Eliminar'}
                              ><Trash2 size={13} /></button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            {/* Derecha: form de edición inline (cuando hay editingLote) */}
            {editingLote && (
              <div className="w-80 border-2 rounded-xl p-4 flex flex-col flex-shrink-0" style={{ borderColor: theme.primary, backgroundColor: theme.primaryLight }}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <FileEdit size={16} style={{ color: theme.primary }} />
                    <h3 className="font-bold text-gray-800 text-sm">Editar lote</h3>
                  </div>
                  <button
                    onClick={() => setEditingLote(null)}
                    className="p-1 hover:bg-white rounded"
                    title="Cancelar"
                    disabled={saving}
                  >
                    <X size={16} className="text-gray-500" />
                  </button>
                </div>

                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Número de lote</label>
                    <input
                      type="text"
                      value={editForm.lote}
                      onChange={(e) => setEditForm(f => ({ ...f, lote: e.target.value }))}
                      className="w-full px-3 py-2 text-sm border-2 border-gray-200 rounded focus:outline-none focus:border-teal-500 font-mono bg-white"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                      Fecha de vencimiento <span className="text-gray-400 font-normal">(opcional)</span>
                    </label>
                    <input
                      type="date"
                      value={editForm.fecha_vencimiento}
                      onChange={(e) => setEditForm(f => ({ ...f, fecha_vencimiento: e.target.value }))}
                      className="w-full px-3 py-2 text-sm border-2 border-gray-200 rounded focus:outline-none focus:border-teal-500 bg-white"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Cantidad (unidades)</label>
                    <input
                      type="number"
                      min="0"
                      value={editForm.cantidad}
                      onChange={(e) => setEditForm(f => ({ ...f, cantidad: e.target.value }))}
                      className="w-full px-3 py-2 text-sm border-2 border-gray-200 rounded focus:outline-none focus:border-teal-500 bg-white text-right font-semibold"
                    />
                    <p className="text-xs text-amber-700 mt-1">
                      <strong>Atención:</strong> al cambiar la cantidad, también se ajusta el stock global del producto y queda registrado en el historial.
                    </p>
                  </div>
                </div>

                <div className="mt-4 flex justify-end gap-2">
                  <button
                    onClick={() => setEditingLote(null)}
                    disabled={saving}
                    className="px-3 py-1.5 bg-white border border-gray-300 rounded text-xs font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleGuardarEdit}
                    disabled={saving}
                    className="px-3 py-1.5 text-xs font-semibold text-white rounded shadow flex items-center gap-1.5 disabled:opacity-50"
                    style={{ backgroundColor: theme.primary }}
                  >
                    {saving ? <Loader size={12} className="animate-spin" /> : <Check size={12} />}
                    Guardar
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="px-6 py-3 border-t bg-gray-50 flex justify-end">
          <button onClick={onClose} className="px-4 py-2 bg-white border border-gray-300 rounded text-sm font-medium text-gray-700 hover:bg-gray-100">
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};


// ─── Modal: alta / edit de lote (manual) ─────────────────────────────────────
const LoteFormModal = ({ open, lote, prefillProductoId, clienteId, productos, theme, onClose, onSaved }) => {
  const esEdicion = !!lote;
  const [productoId, setProductoId] = useState('');
  const [loteNum,    setLoteNum]    = useState('');
  const [fechaVenc,  setFechaVenc]  = useState('');
  const [cantidad,   setCantidad]   = useState('');
  const [saving,     setSaving]     = useState(false);
  const [error,      setError]      = useState('');

  useEffect(() => {
    if (!open) return;
    setError('');
    if (esEdicion) {
      setProductoId(String(lote.producto_id));
      setLoteNum(lote.lote || '');
      setFechaVenc(lote.fecha_vencimiento ? lote.fecha_vencimiento.slice(0, 10) : '');
      setCantidad(String(lote.cantidad ?? 0));
    } else {
      setProductoId(prefillProductoId ? String(prefillProductoId) : '');
      setLoteNum('');
      setFechaVenc('');
      setCantidad('');
    }
  }, [open, lote, prefillProductoId, esEdicion]);

  if (!open) return null;

  const handleGuardar = async () => {
    setError('');
    if (!esEdicion) {
      if (!productoId)               { setError('Selecciona un producto'); return; }
      if (!loteNum.trim())           { setError('Número de lote requerido'); return; }
      const qty = parseInt(cantidad);
      if (!Number.isFinite(qty) || qty < 0) { setError('Cantidad inválida'); return; }
    }

    setSaving(true);
    try {
      let r, d;
      if (esEdicion) {
        const body = {
          lote: loteNum.trim() || undefined,
          fecha_vencimiento: fechaVenc || null,
          cantidad: cantidad !== '' ? Math.max(0, parseInt(cantidad) || 0) : undefined,
        };
        r = await fetch(`${API_URL}/api/stock/lotes/${lote.id}`, {
          method: 'PATCH', headers: authHeaders(), body: JSON.stringify(body),
        });
      } else {
        r = await fetch(`${API_URL}/api/stock/cliente/${clienteId}/lotes`, {
          method: 'POST', headers: authHeaders(),
          body: JSON.stringify({
            producto_id: parseInt(productoId),
            lote: loteNum.trim(),
            fecha_vencimiento: fechaVenc || null,
            cantidad: parseInt(cantidad) || 0,
          }),
        });
      }
      d = await r.json();
      if (!r.ok) throw new Error(d.message || 'Error');
      onSaved();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[55] flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b" style={{ backgroundColor: theme.primaryLight }}>
          <h2 className="text-lg font-bold text-gray-800">{esEdicion ? 'Editar lote' : 'Nuevo lote'}</h2>
          <button onClick={onClose} className="p-2 hover:bg-white rounded-lg"><X size={20} className="text-gray-500" /></button>
        </div>

        {error && (
          <div className="mx-6 mt-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700 flex items-start gap-2">
            <AlertCircle size={16} className="mt-0.5" /><span>{error}</span>
          </div>
        )}

        <div className="p-6 space-y-3">
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Producto</label>
            <select
              value={productoId}
              onChange={(e) => setProductoId(e.target.value)}
              disabled={esEdicion}
              className="w-full px-3 py-2 text-sm border-2 border-gray-200 rounded focus:outline-none focus:border-teal-500 disabled:bg-gray-100"
            >
              <option value="">Selecciona...</option>
              {productos.map(p => (
                <option key={p.producto_id} value={p.producto_id}>
                  {p.nombre} {p.ref_proveedor ? `(${p.ref_proveedor})` : ''}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Número de lote</label>
            <input
              type="text"
              value={loteNum}
              onChange={(e) => setLoteNum(e.target.value)}
              placeholder="Ej: L240115A"
              className="w-full px-3 py-2 text-sm border-2 border-gray-200 rounded focus:outline-none focus:border-teal-500 font-mono"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Fecha de vencimiento <span className="text-gray-400 font-normal">(opcional)</span>
            </label>
            <input
              type="date"
              value={fechaVenc}
              onChange={(e) => setFechaVenc(e.target.value)}
              className="w-full px-3 py-2 text-sm border-2 border-gray-200 rounded focus:outline-none focus:border-teal-500"
            />
            <p className="text-xs text-gray-500 mt-1">Si el producto no caduca, déjalo vacío.</p>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Cantidad (unidades)</label>
            <input
              type="number"
              min="0"
              value={cantidad}
              onChange={(e) => setCantidad(e.target.value)}
              placeholder="0"
              className="w-full px-3 py-2 text-sm border-2 border-gray-200 rounded focus:outline-none focus:border-teal-500"
            />
            {!esEdicion && (
              <p className="text-xs text-gray-500 mt-1">
                <strong>Atención:</strong> esto crea el lote pero NO suma al stock global del producto.
                Para sumar al stock, usa el escaneo de códigos de barras desde la aplicación móvil.
              </p>
            )}
            {esEdicion && (
              <p className="text-xs text-amber-600 mt-1">
                <strong>Atención:</strong> editar la cantidad NO actualiza el stock global del producto.
              </p>
            )}
          </div>
        </div>

        <div className="px-6 py-3 border-t bg-gray-50 flex justify-end gap-2">
          <button onClick={onClose} disabled={saving} className="px-4 py-2 bg-white border border-gray-300 rounded text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50">
            Cancelar
          </button>
          <button
            onClick={handleGuardar}
            disabled={saving}
            className="px-4 py-2 text-sm font-semibold text-white rounded shadow flex items-center gap-2 disabled:opacity-50"
            style={{ backgroundColor: theme.primary }}
          >
            {saving ? <Loader size={14} className="animate-spin" /> : <Check size={14} />}
            {esEdicion ? 'Guardar cambios' : 'Crear lote'}
          </button>
        </div>
      </div>
    </div>
  );
};


// ─── Modal: importar lotes iniciales por Excel (clientes con escaneo) ───────
// ─── Panel: importación de mínimos y máximos por Excel ───────────────────────
// Autocontenido (sin overlay propio): se renderiza dentro de la pestaña
// "Mínimos y máximos" del modal de subida masiva. A diferencia del Excel de
// stock completo, NO toca la columna cantidad: el stock lo gobiernan los lotes.
const MinMaxExcelPanel = ({ cliente, theme, onClose, onApplied }) => {
  const [step, setStep] = useState('idle'); // idle | uploading | preview | applying
  const [preview, setPreview] = useState(null);
  const [confirmed, setConfirmed] = useState({}); // { producto_id: true } para pendientes
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [mostrarAyuda, setMostrarAyuda] = useState(false); // toggle del panel "¿Cómo funciona?"
  const token = localStorage.getItem('token');

  const handleDownload = async () => {
    if (!cliente) return;
    setDownloading(true);
    setError('');
    try {
      const r = await fetch(`${API_URL}/api/stock/cliente/${cliente.id}/excel-minmax`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error('Error al descargar plantilla');
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `minmax_${(cliente.razon_social || 'cliente').replace(/[^a-zA-Z0-9]/g, '_')}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message);
    } finally {
      setDownloading(false);
    }
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !cliente) return;
    setStep('uploading');
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await fetch(`${API_URL}/api/stock/cliente/${cliente.id}/excel-minmax/preview`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.message || 'Error al procesar Excel');
      setPreview(data);
      setConfirmed({});
      setStep('preview');
    } catch (err) {
      setError(err.message);
      setStep('idle');
    }
  };

  const togglePendiente = (id) => setConfirmed(prev => ({ ...prev, [id]: !prev[id] }));
  const todoConfirmado = preview && preview.pendientes.length > 0 &&
    preview.pendientes.every(p => confirmed[p.producto_id]);

  const handleApply = async () => {
    if (!preview) return;
    setStep('applying');
    try {
      const cambios = [
        ...preview.automaticos.map(p => ({ producto_id: p.producto_id, ...p.nuevo, motivo: 'Importacion Excel min/max' })),
        ...preview.pendientes
          .filter(p => confirmed[p.producto_id])
          .map(p => ({ producto_id: p.producto_id, ...p.nuevo, motivo: 'Importacion Excel min/max (confirmado)' })),
      ];
      if (cambios.length === 0) {
        setError('No hay cambios para aplicar');
        setStep('preview');
        return;
      }
      const r = await fetch(`${API_URL}/api/stock/cliente/${cliente.id}/bulk`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ cambios }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.message || 'Error al aplicar');
      onApplied?.(data);
      setStep('idle');
      setPreview(null);
      setConfirmed({});
    } catch (e) {
      setError(e.message);
      setStep('preview');
    }
  };

  const nAConfirmar = Object.values(confirmed).filter(Boolean).length;

  return (
    <div className="flex flex-col min-h-0 flex-1">
      <div className="flex-1 overflow-y-auto p-6">

        {step === 'idle' && (
          <div className="flex gap-4 items-stretch">
            {/* Izquierda: dos cards apilados (descargar / subir) */}
            <div className="flex-1 flex flex-col gap-3 min-w-0">
              <div className="border-2 border-gray-200 rounded-xl p-5">
                <div className="flex items-start gap-3 mb-3">
                  <div className="p-2 rounded-lg" style={{ backgroundColor: theme.primaryLight }}>
                    <Download size={20} style={{ color: theme.primary }} />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-800">1. Descargar plantilla</h3>
                    <p className="text-xs text-gray-500 mt-1">Trae el catálogo con los mínimos y máximos actuales de cada producto.</p>
                  </div>
                </div>
                <button
                  onClick={handleDownload}
                  disabled={downloading}
                  className="w-full px-4 py-2.5 text-white text-sm font-semibold rounded-lg flex items-center justify-center gap-2 disabled:opacity-50"
                  style={{ backgroundColor: theme.primary }}
                >
                  {downloading ? <Loader size={16} className="animate-spin" /> : <Download size={16} />}
                  Descargar plantilla
                </button>
              </div>

              <div className="border-2 border-gray-200 rounded-xl p-5">
                <div className="flex items-start gap-3 mb-3">
                  <div className="p-2 rounded-lg" style={{ backgroundColor: theme.primaryLight }}>
                    <Upload size={20} style={{ color: theme.primary }} />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-800">2. Subir Excel modificado</h3>
                    <p className="text-xs text-gray-500 mt-1">Edita las columnas <strong>minimo</strong> y <strong>maximo</strong> y súbelo.</p>
                  </div>
                </div>
                <label className="w-full px-4 py-2.5 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center gap-2 cursor-pointer hover:bg-gray-50 text-sm text-gray-700">
                  <Upload size={16} />
                  Elegir archivo .xlsx
                  <input type="file" accept=".xlsx" className="hidden" onChange={handleFile} />
                </label>
              </div>
            </div>

            {/* Derecha: botón ? compacto (cuando ayuda cerrada) o panel completo (cuando abierta) */}
            {!mostrarAyuda ? (
              <button
                onClick={() => setMostrarAyuda(true)}
                className="w-28 border-2 border-gray-200 rounded-xl p-5 flex flex-col items-center justify-center gap-2 hover:bg-gray-50 transition-colors group flex-shrink-0"
                title="¿Cómo funciona la importación de mínimos y máximos?"
              >
                <div className="p-3 rounded-full transform group-hover:scale-110 transition-transform" style={{ backgroundColor: theme.primaryLight }}>
                  <HelpCircle size={28} style={{ color: theme.primary }} />
                </div>
                <span className="text-xs font-semibold text-gray-700 text-center">¿Cómo funciona?</span>
              </button>
            ) : (
              <div className="w-96 border-2 rounded-xl p-4 flex flex-col flex-shrink-0 overflow-y-auto" style={{ borderColor: theme.primary, backgroundColor: theme.primaryLight }}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <HelpCircle size={18} style={{ color: theme.primary }} />
                    <h3 className="font-bold text-gray-800 text-sm">¿Cómo funciona?</h3>
                  </div>
                  <button
                    onClick={() => setMostrarAyuda(false)}
                    className="p-1 hover:bg-white rounded"
                    title="Cerrar ayuda"
                  >
                    <X size={16} className="text-gray-500" />
                  </button>
                </div>

                <div className="space-y-3 text-xs text-gray-700">
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-1">El proceso en 3 pasos</h4>
                    <ol className="list-decimal list-inside space-y-1 pl-1">
                      <li><strong>Descarga</strong> la plantilla. Viene con todos los productos del catálogo y sus mínimos/máximos actuales.</li>
                      <li><strong>Rellena</strong> las columnas <em>minimo</em> y <em>maximo</em>. El resto (producto_id, nombre, ref, proveedor) ya viene y no se edita.</li>
                      <li><strong>Sube</strong> el archivo. Verás una vista previa con los cambios antes de aplicar nada.</li>
                    </ol>
                  </div>

                  <div className="border-t pt-2" style={{ borderColor: theme.primaryBorder || 'rgba(60, 191, 154, 0.20)' }}>
                    <h4 className="font-semibold text-gray-800 mb-1">Lo que debes saber</h4>
                    <ul className="list-disc list-inside space-y-1 pl-1">
                      <li>Este Excel <strong>solo cambia mínimos y máximos</strong>. NO toca la cantidad en stock: en este cliente la gobiernan los <strong>lotes</strong> (pestaña «Lotes»).</li>
                      <li>El <strong>mínimo</strong> dispara las alertas de reposición; el <strong>máximo</strong> marca el sobrestock.</li>
                      <li>Los cambios "seguros" (subir o ajustar valores) se aplican automáticamente.</li>
                      <li>Cuando un valor pasa de <strong>X &gt; 0 a 0</strong>, requiere confirmación manual (puede ser un olvido en la celda).</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {step === 'uploading' && (
          <div className="p-12 text-center text-gray-500">
            <Loader size={32} className="animate-spin mx-auto mb-3" />
            <p className="text-sm">Analizando el Excel...</p>
          </div>
        )}

        {step === 'preview' && preview && (
          <>
            <div className="grid grid-cols-4 gap-3 mb-4">
              <Stat label="Filas leídas" value={preview.total_filas} />
              <Stat label="Automáticos" value={preview.automaticos.length} color={theme.primary} />
              <Stat label="Pendientes" value={preview.pendientes.length} color="#d97706" />
              <Stat label="Sin cambios" value={preview.sin_cambios} muted />
            </div>

            {preview.automaticos.length > 0 && (
              <div className="mb-5">
                <h3 className="text-sm font-bold text-gray-800 mb-2 flex items-center gap-2">
                  <Check size={14} style={{ color: theme.primary }} />
                  Cambios automáticos ({preview.automaticos.length})
                </h3>
                <div className="border rounded-lg overflow-hidden max-h-64 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="text-left px-3 py-2">Producto</th>
                        <th className="text-right px-3 py-2">Mín.</th>
                        <th className="text-right px-3 py-2">Máx.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.automaticos.map(p => (
                        <tr key={p.producto_id} className="border-t">
                          <td className="px-3 py-1.5 truncate max-w-[360px]" title={p.nombre}>{p.nombre}</td>
                          <td className="px-3 py-1.5 text-right">{renderChange(p.actual.minimo, p.nuevo.minimo)}</td>
                          <td className="px-3 py-1.5 text-right">{renderChange(p.actual.maximo, p.nuevo.maximo)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {preview.pendientes.length > 0 && (
              <div className="mb-2">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2">
                    <AlertTriangle size={14} className="text-amber-600" />
                    Requieren verificación manual ({preview.pendientes.length})
                  </h3>
                  <label className="text-xs text-gray-600 flex items-center gap-1 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={todoConfirmado}
                      onChange={() => {
                        const all = !todoConfirmado;
                        const next = {};
                        preview.pendientes.forEach(p => { next[p.producto_id] = all; });
                        setConfirmed(next);
                      }}
                    /> Marcar todos
                  </label>
                </div>
                <p className="text-xs text-gray-500 mb-2">
                  Estos cambios mandan mínimo o máximo a 0 desde un valor mayor. Marca solo los que confirmes manualmente.
                </p>
                <div className="border-2 border-amber-300 rounded-lg overflow-hidden max-h-64 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-amber-50 sticky top-0">
                      <tr>
                        <th className="text-center px-2 py-2 w-10">✓</th>
                        <th className="text-left px-3 py-2">Producto</th>
                        <th className="text-right px-3 py-2">Mín.</th>
                        <th className="text-right px-3 py-2">Máx.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.pendientes.map(p => (
                        <tr key={p.producto_id} className="border-t">
                          <td className="px-2 py-1.5 text-center">
                            <input
                              type="checkbox"
                              checked={!!confirmed[p.producto_id]}
                              onChange={() => togglePendiente(p.producto_id)}
                            />
                          </td>
                          <td className="px-3 py-1.5 truncate max-w-[320px]" title={p.nombre}>{p.nombre}</td>
                          <td className={`px-3 py-1.5 text-right ${p.flags.minimo ? 'bg-amber-50 font-bold' : ''}`}>{renderChange(p.actual.minimo, p.nuevo.minimo)}</td>
                          <td className={`px-3 py-1.5 text-right ${p.flags.maximo ? 'bg-amber-50 font-bold' : ''}`}>{renderChange(p.actual.maximo, p.nuevo.maximo)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {preview.automaticos.length === 0 && preview.pendientes.length === 0 && (
              <div className="p-8 text-center text-gray-500 text-sm">
                El Excel no introduce ningún cambio de mínimos o máximos respecto al estado actual.
              </div>
            )}
          </>
        )}

        {step === 'applying' && (
          <div className="p-12 text-center text-gray-500">
            <Loader size={32} className="animate-spin mx-auto mb-3" />
            <p className="text-sm">Aplicando cambios...</p>
          </div>
        )}

        {error && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg">
            {error}
          </div>
        )}
      </div>

      {/* Footer: Cancelar siempre presente (igual que la pestaña de lotes) */}
      <div className="px-6 py-3 border-t bg-gray-50 flex items-center justify-between">
        <div className="text-xs text-gray-500">
          {step === 'preview' && preview && (
            <span>{preview.automaticos.length + nAConfirmar} cambio(s) listos para aplicar</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onClose}
            disabled={step === 'uploading' || step === 'applying'}
            className="px-4 py-2 bg-white border border-gray-300 rounded text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50"
          >
            Cancelar
          </button>
          {step === 'preview' && preview && (
            <>
              <button
                onClick={() => { setStep('idle'); setPreview(null); }}
                className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-200 rounded"
              >Volver</button>
              <button
                onClick={handleApply}
                disabled={preview.automaticos.length === 0 && nAConfirmar === 0}
                className="px-5 py-2 text-sm font-semibold text-white rounded shadow disabled:opacity-50 flex items-center gap-2"
                style={{ backgroundColor: theme.primary }}
              >
                <Check size={14} />
                Aplicar cambios ({preview.automaticos.length + nAConfirmar})
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};


const ExcelLotesModal = ({ open, cliente, theme, onClose, onApplied }) => {
  const [tab, setTab] = useState('minmax'); // 'minmax' | 'lotes'
  const [step, setStep] = useState('idle'); // idle | uploading | preview | applying | done
  const [downloading, setDownloading] = useState(false);
  const [preview, setPreview] = useState(null); // { filas, resumen }
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [filtroError, setFiltroError] = useState(false); // ver solo errores en preview
  const [mostrarAyuda, setMostrarAyuda] = useState(false); // toggle del panel de ayuda en el mismo modal
  const [revalidando, setRevalidando] = useState(false);
  const fileInputRef = useRef(null);

  // Editar una celda de una fila en la vista previa. Marca la fila como _dirty.
  const editarCelda = (idx, campo, valor) => {
    setPreview(prev => {
      if (!prev) return prev;
      const filas = [...prev.filas];
      filas[idx] = { ...filas[idx], [campo]: valor, _dirty: true };
      return { ...prev, filas };
    });
  };

  // Reenvía al backend solo las filas modificadas para volver a validarlas.
  const handleRevalidar = async () => {
    if (!preview) return;
    const dirtyIdxs = preview.filas
      .map((f, i) => (f._dirty ? i : -1))
      .filter(i => i >= 0);
    if (dirtyIdxs.length === 0) return;
    setRevalidando(true);
    setError('');
    try {
      const payload = dirtyIdxs.map(i => preview.filas[i]);
      const r = await fetch(`${API_URL}/api/stock/cliente/${cliente.id}/lotes/excel/validar-filas`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ filas: payload }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.message || 'Error al revalidar');
      // Reemplaza las filas dirty con su nueva versión validada
      setPreview(prev => {
        if (!prev) return prev;
        const filas = [...prev.filas];
        dirtyIdxs.forEach((origIdx, pos) => {
          filas[origIdx] = { ...data.filas[pos], _dirty: false };
        });
        // Recalcular resumen
        const resumen = {
          total: filas.length,
          ok: filas.filter(f => f.status === 'ok').length,
          error: filas.filter(f => f.status === 'error').length,
          crear: filas.filter(f => f.status === 'ok' && f.accion === 'crear').length,
          sumar: filas.filter(f => f.status === 'ok' && f.accion === 'sumar').length,
          unidades_total: filas.filter(f => f.status === 'ok').reduce((s, f) => s + (f.cantidad_unidades || 0), 0),
        };
        return { ...prev, filas, resumen };
      });
    } catch (e) {
      setError(e.message);
    } finally {
      setRevalidando(false);
    }
  };

  const dirtyCount = preview ? preview.filas.filter(f => f._dirty).length : 0;

  const token = localStorage.getItem('token');

  useEffect(() => {
    if (open) {
      setTab('minmax');
      setStep('idle'); setPreview(null); setResult(null); setError(''); setFiltroError(false); setMostrarAyuda(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [open]);

  if (!open || !cliente) return null;

  const handleDescargar = async () => {
    setDownloading(true);
    setError('');
    try {
      const r = await fetch(`${API_URL}/api/stock/cliente/${cliente.id}/lotes/excel`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.message || 'Error al descargar plantilla');
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `lotes_iniciales_${(cliente.razon_social || 'cliente').replace(/[^a-zA-Z0-9]/g, '_')}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message);
    } finally {
      setDownloading(false);
    }
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setStep('uploading');
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await fetch(`${API_URL}/api/stock/cliente/${cliente.id}/lotes/excel/preview`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.message || 'Error al procesar Excel');
      setPreview(data);
      setStep('preview');
    } catch (err) {
      setError(err.message);
      setStep('idle');
    }
  };

  const handleAplicar = async () => {
    if (!preview) return;
    setStep('applying');
    setError('');
    try {
      const r = await fetch(`${API_URL}/api/stock/cliente/${cliente.id}/lotes/excel/aplicar`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ filas: preview.filas }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.message || 'Error al aplicar');
      setResult(data);
      setStep('done');
      if (onApplied) onApplied(data);
    } catch (e) {
      setError(e.message);
      setStep('preview');
    }
  };

  const filasMostradas = preview && filtroError
    ? preview.filas.filter(f => f.status === 'error')
    : (preview?.filas || []);

  // El modal cambia de ancho/alto dinámicamente:
  //   - preview: max-w-5xl + altura fija (la tabla scrollea internamente)
  //   - idle con ayuda abierta: max-w-4xl + altura natural
  //   - resto: max-w-2xl + altura natural
  // Ancho constante para ambas pestañas: así al cambiar de pestaña el modal no
  // "salta" de tamaño. La tabla ancha del preview de lotes scrollea dentro.
  const maxWidth = 'max-w-3xl';
  // Altura: solo el preview de lotes necesita alto fijo (su tabla scrollea
  // verticalmente con flex-1). El resto crece con el contenido (maxHeight).
  const modalStyle = (tab === 'lotes' && step === 'preview')
    ? { height: '80vh' }
    : { maxHeight: '88vh' };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div
        className={`bg-white rounded-2xl shadow-2xl w-full ${maxWidth} overflow-hidden flex flex-col`}
        style={modalStyle}
      >

        <div className="flex items-center justify-between px-6 py-4 border-b" style={{ backgroundColor: theme.primaryLight }}>
          <div className="flex items-center gap-3">
            <FileSpreadsheet size={22} style={{ color: theme.primary }} />
            <div>
              <h2 className="text-lg font-bold text-gray-800">Subida masiva por Excel</h2>
              <p className="text-xs text-gray-600">{cliente?.razon_social}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white rounded-lg"
            disabled={step === 'uploading' || step === 'applying'}
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Pestañas: cada Excel tiene un propósito distinto y bien diferenciado */}
        <div className="flex border-b flex-shrink-0 px-2">
          <button
            onClick={() => setTab('minmax')}
            className="px-5 py-2.5 text-sm font-semibold border-b-2 transition-colors -mb-px"
            style={tab === 'minmax'
              ? { borderColor: theme.primary, color: theme.primary }
              : { borderColor: 'transparent', color: '#6b7280' }}
            title="Definir niveles mínimo y máximo de cada producto"
          >
            Mínimos y máximos
          </button>
          <button
            onClick={() => setTab('lotes')}
            className="px-5 py-2.5 text-sm font-semibold border-b-2 transition-colors -mb-px"
            style={tab === 'lotes'
              ? { borderColor: theme.primary, color: theme.primary }
              : { borderColor: 'transparent', color: '#6b7280' }}
            title="Cargar lotes con su número, vencimiento y cantidad"
          >
            Lotes
          </button>
        </div>

        {tab === 'minmax' ? (
          <MinMaxExcelPanel cliente={cliente} theme={theme} onClose={onClose} onApplied={onApplied} />
        ) : (
        <>
        {error && (
          <div className="mx-6 mt-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700 flex items-start gap-2">
            <AlertCircle size={16} className="mt-0.5" /><span>{error}</span>
          </div>
        )}

        <div className={`flex-1 p-6 min-h-0 ${step === 'preview' ? 'overflow-hidden flex flex-col' : 'overflow-y-auto'}`}>

          {/* Paso 1: descargar + subir (apilados) | ? toggle a la derecha */}
          {step === 'idle' && (
            <div className="flex gap-4 items-stretch">
              {/* Izquierda: dos cards apilados */}
              <div className="flex-1 flex flex-col gap-3 min-w-0">
                <div className="border-2 border-gray-200 rounded-xl p-5">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="p-2 rounded-lg" style={{ backgroundColor: theme.primaryLight }}>
                      <Download size={20} style={{ color: theme.primary }} />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-800">1. Descargar plantilla</h3>
                      <p className="text-xs text-gray-500 mt-1">Incluye los productos del catálogo y una hoja de ayuda.</p>
                    </div>
                  </div>
                  <button
                    onClick={handleDescargar}
                    disabled={downloading}
                    className="w-full px-4 py-2.5 text-white text-sm font-semibold rounded-lg flex items-center justify-center gap-2 disabled:opacity-50"
                    style={{ backgroundColor: theme.primary }}
                  >
                    {downloading ? <Loader size={16} className="animate-spin" /> : <Download size={16} />}
                    Descargar plantilla
                  </button>
                </div>

                <div className="border-2 border-gray-200 rounded-xl p-5">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="p-2 rounded-lg" style={{ backgroundColor: theme.primaryLight }}>
                      <Upload size={20} style={{ color: theme.primary }} />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-800">2. Subir Excel rellenado</h3>
                      <p className="text-xs text-gray-500 mt-1">Verás una vista previa antes de aplicar los cambios.</p>
                    </div>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFile}
                    className="hidden"
                    id="excel-lotes-file"
                  />
                  <label
                    htmlFor="excel-lotes-file"
                    className="w-full px-4 py-2.5 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center gap-2 cursor-pointer hover:bg-gray-50 text-sm text-gray-700"
                  >
                    <Upload size={16} />
                    Elegir archivo .xlsx
                  </label>
                </div>
              </div>

              {/* Derecha: botón ? compacto (cuando ayuda cerrada) o panel completo (cuando abierta) */}
              {!mostrarAyuda ? (
                <button
                  onClick={() => setMostrarAyuda(true)}
                  className="w-28 border-2 border-gray-200 rounded-xl p-5 flex flex-col items-center justify-center gap-2 hover:bg-gray-50 transition-colors group flex-shrink-0"
                  title="¿Cómo funciona la importación?"
                >
                  <div className="p-3 rounded-full transform group-hover:scale-110 transition-transform" style={{ backgroundColor: theme.primaryLight }}>
                    <HelpCircle size={28} style={{ color: theme.primary }} />
                  </div>
                  <span className="text-xs font-semibold text-gray-700 text-center">¿Cómo funciona?</span>
                </button>
              ) : (
                <div className="w-96 border-2 rounded-xl p-4 flex flex-col flex-shrink-0 overflow-y-auto" style={{ borderColor: theme.primary, backgroundColor: theme.primaryLight }}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <HelpCircle size={18} style={{ color: theme.primary }} />
                      <h3 className="font-bold text-gray-800 text-sm">¿Cómo funciona?</h3>
                    </div>
                    <button
                      onClick={() => setMostrarAyuda(false)}
                      className="p-1 hover:bg-white rounded"
                      title="Cerrar ayuda"
                    >
                      <X size={16} className="text-gray-500" />
                    </button>
                  </div>

                  <div className="space-y-3 text-xs text-gray-700">
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-1">El proceso en 3 pasos</h4>
                      <ol className="list-decimal list-inside space-y-1 pl-1">
                        <li><strong>Descarga</strong> la plantilla. Viene con todos los productos del catálogo ya rellenados.</li>
                        <li><strong>Rellena</strong> las columnas <em>lote</em>, <em>fecha_vencimiento</em> (formato <strong>DD-MM-AAAA</strong>, p. ej. 31-12-2025; también vale DD/MM/AAAA) y <em>cantidad_unidades</em>.</li>
                        <li><strong>Sube</strong> el archivo. Verás una vista previa con cada fila validada antes de aplicar nada.</li>
                      </ol>
                    </div>

                    <div className="border-t pt-2" style={{ borderColor: theme.primaryBorder || 'rgba(60, 191, 154, 0.20)' }}>
                      <h4 className="font-semibold text-gray-800 mb-1">Lo que debes saber</h4>
                      <ul className="list-disc list-inside space-y-1 pl-1">
                        <li><strong>Cantidades en unidades</strong>, no en cajas. 3 cajas de 100 unidades del lote L240115A → cantidad = 300.</li>
                        <li><strong>Un producto puede tener varios lotes</strong>. Una fila por cada lote (con su número, fecha y cantidad).</li>
                        <li><strong>El proveedor es obligatorio</strong>. Identificamos el producto por <em>ref_proveedor + proveedor</em>; si la referencia está vacía, por <em>nombre + proveedor</em>.</li>
                        <li>Si el producto no caduca, deja la fecha vacía.</li>
                        <li>Si un lote ya existe, la cantidad se SUMA al existente.</li>
                        <li>Cada cambio queda en el historial con motivo "Carga inicial por Excel".</li>
                      </ul>
                    </div>

                  </div>
                </div>
              )}
            </div>
          )}

          {step === 'uploading' && (
            <div className="text-center py-12">
              <Loader size={36} className="animate-spin mx-auto text-gray-400 mb-4" />
              <p className="text-sm text-gray-600">Validando el archivo, espera unos segundos...</p>
            </div>
          )}

          {/* Paso 2: vista previa */}
          {step === 'preview' && preview && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4 flex-shrink-0">
                <div className="bg-gray-50 border rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-gray-800">{preview.resumen.total}</div>
                  <div className="text-xs text-gray-500 mt-0.5">Filas leídas</div>
                </div>
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-emerald-700">{preview.resumen.ok}</div>
                  <div className="text-xs text-emerald-600 mt-0.5">Válidas</div>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-red-700">{preview.resumen.error}</div>
                  <div className="text-xs text-red-600 mt-0.5">Con errores</div>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-blue-700">{preview.resumen.crear} / {preview.resumen.sumar}</div>
                  <div className="text-xs text-blue-600 mt-0.5">Crear / Sumar</div>
                </div>
                <div className="bg-teal-50 border border-teal-200 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-teal-700">{preview.resumen.unidades_total}</div>
                  <div className="text-xs text-teal-600 mt-0.5">Unidades totales</div>
                </div>
              </div>

              <div className="flex items-center justify-between mb-3 gap-3 flex-wrap flex-shrink-0">
                {preview.resumen.error > 0 ? (
                  <label className="inline-flex items-center gap-2 text-xs text-gray-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={filtroError}
                      onChange={(e) => setFiltroError(e.target.checked)}
                    />
                    Ver solo filas con errores
                  </label>
                ) : <span />}
                {dirtyCount > 0 && (
                  <button
                    onClick={handleRevalidar}
                    disabled={revalidando}
                    className="px-3 py-1.5 text-xs font-semibold text-white rounded shadow flex items-center gap-1.5 disabled:opacity-50"
                    style={{ backgroundColor: theme.primary }}
                  >
                    {revalidando ? <Loader size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                    Re-validar {dirtyCount} fila(s) modificada(s)
                  </button>
                )}
              </div>

              {/* Tabla con altura flexible — toma todo el espacio restante del modal */}
              <div
                className="border border-gray-200 rounded-lg overflow-auto"
                style={{ flex: '1 1 0%', minHeight: 0 }}
              >
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 text-gray-600 sticky top-0 z-10">
                    <tr>
                      <th className="text-left px-2 py-2 w-10">#</th>
                      <th className="text-left px-2 py-2 w-44">Proveedor</th>
                      <th className="text-left px-2 py-2 w-32">Ref.</th>
                      <th className="text-left px-2 py-2 min-w-[200px]">Producto</th>
                      <th className="text-left px-2 py-2 w-28">Lote</th>
                      <th className="text-left px-2 py-2 w-36">Vencimiento</th>
                      <th className="text-right px-2 py-2 w-20">Unidades</th>
                      <th className="text-left px-2 py-2 w-44">Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filasMostradas.map((f) => {
                      // Encontramos el indice REAL en preview.filas (no el de filasMostradas)
                      // para que las ediciones apunten al sitio correcto al filtrar.
                      const realIdx = preview.filas.indexOf(f);
                      const baseInput = "w-full px-1.5 py-1 border border-transparent rounded text-xs focus:outline-none focus:border-gray-400 bg-transparent hover:bg-white";
                      return (
                        <tr key={realIdx} className={`border-t ${f.status === 'error' ? 'bg-red-50' : 'hover:bg-gray-50'}`}>
                          <td className="px-2 py-1 text-gray-500 align-top pt-2">
                            <div className="flex items-center gap-1">
                              {f._dirty && <span className="w-1.5 h-1.5 rounded-full bg-amber-500" title="Modificada"></span>}
                              {f.fila || '—'}
                            </div>
                          </td>
                          <td className="px-1 py-1">
                            <input
                              type="text"
                              value={f.proveedor_input || ''}
                              onChange={(e) => editarCelda(realIdx, 'proveedor_input', e.target.value)}
                              className={baseInput}
                            />
                          </td>
                          <td className="px-1 py-1">
                            <input
                              type="text"
                              value={f.ref_proveedor || ''}
                              onChange={(e) => editarCelda(realIdx, 'ref_proveedor', e.target.value)}
                              className={`${baseInput} font-mono`}
                            />
                          </td>
                          <td className="px-1 py-1">
                            <input
                              type="text"
                              value={f.producto_nombre || ''}
                              onChange={(e) => editarCelda(realIdx, 'producto_nombre', e.target.value)}
                              className={baseInput}
                              title={f.producto_nombre}
                            />
                          </td>
                          <td className="px-1 py-1">
                            <input
                              type="text"
                              value={f.lote || ''}
                              onChange={(e) => editarCelda(realIdx, 'lote', e.target.value)}
                              className={`${baseInput} font-mono`}
                            />
                          </td>
                          <td className="px-1 py-1">
                            <input
                              type="date"
                              value={f.fecha_vencimiento || (typeof f.fecha_input === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(f.fecha_input) ? f.fecha_input : '')}
                              onChange={(e) => editarCelda(realIdx, 'fecha_input', e.target.value)}
                              className={baseInput}
                            />
                          </td>
                          <td className="px-1 py-1">
                            <input
                              type="number"
                              min="0"
                              value={f.cantidad_unidades ?? ''}
                              onChange={(e) => editarCelda(realIdx, 'cantidad_unidades', e.target.value === '' ? null : parseInt(e.target.value))}
                              className={`${baseInput} text-right font-semibold`}
                            />
                          </td>
                          <td className="px-2 py-1.5 align-top pt-2">
                            {f._dirty ? (
                              <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-medium bg-amber-100 text-amber-700">
                                <AlertTriangle size={10} />
                                Sin re-validar
                              </span>
                            ) : f.status === 'ok' ? (
                              <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-medium bg-emerald-100 text-emerald-700">
                                <Check size={10} />
                                {f.accion === 'crear' ? 'Crear lote' : 'Sumar a lote'}
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-medium bg-red-100 text-red-700" title={f.mensaje}>
                                <AlertCircle size={10} />
                                {f.mensaje && f.mensaje.length > 30 ? f.mensaje.slice(0, 30) + '…' : f.mensaje}
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <p className="text-xs text-gray-500 mt-3 flex-shrink-0">
                Solo se aplicarán las filas marcadas como <strong className="text-emerald-700">válidas</strong>. Las filas con errores o sin re-validar se ignoran.
                {dirtyCount > 0 && <span className="text-amber-700 font-medium"> · Tienes {dirtyCount} fila(s) modificadas que necesitan re-validación.</span>}
              </p>
            </>
          )}

          {step === 'applying' && (
            <div className="text-center py-12">
              <Loader size={36} className="animate-spin mx-auto text-gray-400 mb-4" />
              <p className="text-sm text-gray-600">Aplicando los cambios...</p>
            </div>
          )}

          {/* Paso 3: resumen final */}
          {step === 'done' && result && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 text-center">
              <Check size={40} className="text-emerald-600 mx-auto mb-3" />
              <h3 className="text-lg font-bold text-emerald-800 mb-4">Importación completada</h3>
              <div className="grid grid-cols-3 gap-3 max-w-md mx-auto">
                <div className="bg-white rounded p-3">
                  <div className="text-2xl font-bold text-emerald-700">{result.lotes_creados}</div>
                  <div className="text-xs text-gray-500">Lotes nuevos</div>
                </div>
                <div className="bg-white rounded p-3">
                  <div className="text-2xl font-bold text-blue-700">{result.lotes_actualizados}</div>
                  <div className="text-xs text-gray-500">Lotes sumados</div>
                </div>
                <div className="bg-white rounded p-3">
                  <div className="text-2xl font-bold text-teal-700">{result.unidades_sumadas}</div>
                  <div className="text-xs text-gray-500">Unidades</div>
                </div>
              </div>
            </div>
          )}

        </div>

        {/* Footer con acciones */}
        <div className="px-6 py-3 border-t bg-gray-50 flex items-center justify-between">
          <div className="text-xs text-gray-500">
            {step === 'preview' && preview && (
              <span>{preview.resumen.ok} fila(s) listas para aplicar</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              disabled={step === 'uploading' || step === 'applying'}
              className="px-4 py-2 bg-white border border-gray-300 rounded text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50"
            >
              {step === 'done' ? 'Cerrar' : 'Cancelar'}
            </button>
            {step === 'preview' && preview && preview.resumen.ok > 0 && (
              <button
                onClick={handleAplicar}
                disabled={dirtyCount > 0}
                title={dirtyCount > 0 ? 'Re-valida las filas modificadas antes de aplicar' : ''}
                className="px-5 py-2 text-sm font-semibold text-white rounded shadow flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: theme.primary }}
              >
                <Check size={14} />
                Aplicar {preview.resumen.ok} fila(s)
              </button>
            )}
          </div>
        </div>
        </>
        )}
      </div>
    </div>
  );
};


// ─── Componente: panel central con la tabla editable ─────────────────────────
const MainPanel = ({
  clienteSel, productos, productosFiltrados, pageItems, pageStart, page, setPage,
  totalPages, PAGE_SIZE, loading, busqueda, setBusqueda,
  proveedorFiltro, setProveedorFiltro, proveedoresUnicos,
  cambios, valorActual,
  onCellChange, hayCambios, guardarCambios, saving, theme,
  onVerMovimientosProducto, onExcelOpen, onExcelLotesOpen,
  movimientos, loadingMovimientos, filtroMovProducto, onClearFiltroMov,
  pestaña, setPestaña, pendientes, onAceptarPendiente, onRechazarPendiente,
  modoStockCliente, lotes, onAbrirLotesProducto, onNuevoLote, onEditarLote, onEliminarLote,
  albaranesMixto = [], onAbrirAlbaranMixto,
  // Vista Unidades/Cajas. Se define en el componente padre porque el estado
  // vive allí junto al de `cambios`: cambiar de modo se bloquea si hay
  // ediciones sin guardar, y esa comprobación necesita ambos.
  vistaStock = 'unidades', enCajas = false, cambiarVista = () => {},
  factorDe = () => 1, mostrarValor, tooltipCajas = () => '', tooltipUmbral = () => '',
}) => {
  if (!clienteSel) return null;
  const esPicking  = modoStockCliente === 'picking';
  const esAlbaran  = modoStockCliente === 'albaran';
  const esMixto    = modoStockCliente === 'mixto';

  return (
    <div className="h-full p-4 flex flex-col overflow-hidden">
      {/* Cabecera compacta del cliente + acciones */}
      <div className="bg-white rounded-lg shadow-sm border px-4 py-2.5 mb-3 flex items-center justify-between gap-4 flex-wrap flex-shrink-0">
        <div className="min-w-0">
          <div className="flex items-baseline gap-2">
            <h1 className="text-base font-bold text-gray-800 truncate">{clienteSel.razon_social}</h1>
            <span className="text-xs text-gray-400">· {clienteSel.localidad || '—'}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={(esPicking || esAlbaran || esMixto) ? onExcelLotesOpen : onExcelOpen}
            className="px-4 py-2.5 text-sm font-semibold rounded-lg border-2 flex items-center gap-2 transition-colors"
            style={{ color: theme.primary, borderColor: theme.primary, backgroundColor: theme.primaryLight }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = theme.primary; e.currentTarget.style.color = '#ffffff'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = theme.primaryLight; e.currentTarget.style.color = theme.primary; }}
            title="Subir stock de forma masiva desde un archivo Excel"
          >
            <FileSpreadsheet size={16} /> Subida masiva de stock por Excel
          </button>
          <button
            onClick={guardarCambios}
            disabled={!hayCambios || saving}
            className="px-5 py-2.5 text-sm font-semibold text-white rounded-lg shadow flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed transform transition-transform hover:scale-[1.02]"
            style={{ backgroundColor: theme.primary }}
          >
            {saving ? <Loader size={16} className="animate-spin" /> : <Save size={16} />}
            Guardar ({Object.keys(cambios).length})
          </button>
        </div>
      </div>

      {/* Card único que contiene tabs + filtros + tabla scrolleable + footer */}
      <div className="bg-white rounded-lg shadow-sm border flex-1 flex flex-col overflow-hidden min-h-0">
        {/* Tabs integrados al card */}
        <div className="flex border-b flex-shrink-0">
          <button
            onClick={() => setPestaña('stock')}
            className="px-5 py-2.5 text-sm font-semibold border-b-2 transition-colors -mb-px"
            style={pestaña === 'stock'
              ? { borderColor: theme.primary, color: theme.primary }
              : { borderColor: 'transparent', color: '#6b7280' }}
          >
            Stock
          </button>
          {/* Tab legacy 'Pendientes de verificación': SOLO visible si hay
              datos históricos en stock_pendiente_verificacion. El flujo nuevo
              de albarán pasa por la tab unificada 'Albaranes pendientes'. */}
          {esAlbaran && pendientes.length > 0 && (
            <button
              onClick={() => setPestaña('pendientes')}
              className="px-5 py-2.5 text-sm font-semibold border-b-2 transition-colors -mb-px flex items-center gap-2"
              style={pestaña === 'pendientes'
                ? { borderColor: theme.primary, color: theme.primary }
                : { borderColor: 'transparent', color: '#6b7280' }}
              title="Pendientes históricos (sistema antiguo)"
            >
              Pendientes (legacy)
              <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-gray-200 text-gray-700">
                {pendientes.length}
              </span>
            </button>
          )}
          {(esPicking || esAlbaran || esMixto) && (
            <button
              onClick={() => setPestaña('lotes')}
              className="px-5 py-2.5 text-sm font-semibold border-b-2 transition-colors -mb-px flex items-center gap-2"
              style={pestaña === 'lotes'
                ? { borderColor: theme.primary, color: theme.primary }
                : { borderColor: 'transparent', color: '#6b7280' }}
            >
              Lotes
              {lotes && lotes.length > 0 && (
                <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-teal-100 text-teal-700">
                  {lotes.length}
                </span>
              )}
            </button>
          )}
          {(esAlbaran || esMixto) && (
            <button
              onClick={() => setPestaña('albaranes-mixto')}
              className="px-5 py-2.5 text-sm font-semibold border-b-2 transition-colors -mb-px flex items-center gap-2"
              style={pestaña === 'albaranes-mixto'
                ? { borderColor: theme.primary, color: theme.primary }
                : { borderColor: 'transparent', color: '#6b7280' }}
            >
              <FileText size={13} />
              Albaranes pendientes
              {albaranesMixto.length > 0 && (
                <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-700">
                  {albaranesMixto.length}
                </span>
              )}
            </button>
          )}
          <button
            onClick={() => setPestaña('movimientos')}
            className="px-5 py-2.5 text-sm font-semibold border-b-2 transition-colors -mb-px flex items-center gap-2"
            style={pestaña === 'movimientos'
              ? { borderColor: theme.primary, color: theme.primary }
              : { borderColor: 'transparent', color: '#6b7280' }}
          >
            <History size={13} />
            Movimientos
          </button>
        </div>

        {pestaña === 'pendientes' && esAlbaran ? (
          <div className="flex-1 overflow-y-auto">
            <PendientesVerificacionPanel
              pendientes={pendientes}
              theme={theme}
              onAceptar={onAceptarPendiente}
              onRechazar={onRechazarPendiente}
            />
          </div>
        ) : pestaña === 'albaranes-mixto' && (esAlbaran || esMixto) ? (
          <div className="flex-1 overflow-y-auto">
            <AlbaranesMixtoPanel
              albaranes={albaranesMixto}
              theme={theme}
              onAbrirAlbaran={onAbrirAlbaranMixto}
            />
          </div>
        ) : pestaña === 'lotes' && (esPicking || esAlbaran || esMixto) ? (
          <LotesPanel
            lotes={lotes || []}
            productos={productos}
            theme={theme}
            onNuevoLote={onNuevoLote}
            onEditarLote={onEditarLote}
            onEliminarLote={onEliminarLote}
            onVerProducto={(l) => onAbrirLotesProducto({ producto_id: l.producto_id, nombre: l.producto_nombre })}
          />
        ) : pestaña === 'movimientos' ? (
          <MovimientosPanel
            movimientos={movimientos}
            loading={loadingMovimientos}
            theme={theme}
            filtroProductoExterno={filtroMovProducto}
            onClearFiltroExterno={onClearFiltroMov}
          />
        ) : (
          <>
            {/* Filtros compactos */}
            <div className="px-4 py-2.5 border-b bg-gray-50/50 flex flex-wrap gap-2 flex-shrink-0">
              <div className="relative flex-1 min-w-[200px]">
                <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none">
                  <Search size={14} className="text-gray-400" />
                </div>
                <input
                  type="text"
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                  placeholder="Buscar por nombre, ref o marca..."
                  className="w-full pr-3 py-1.5 border border-gray-200 rounded text-sm focus:outline-none transition-colors bg-white"
                  style={{ paddingLeft: '32px' }}
                  onFocus={(e) => e.target.style.borderColor = theme.primary}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                />
              </div>
              <div className="w-full md:w-64">
                <select
                  value={proveedorFiltro}
                  onChange={(e) => setProveedorFiltro(e.target.value)}
                  className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm bg-white focus:outline-none transition-colors"
                  onFocus={(e) => e.target.style.borderColor = theme.primary}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                >
                  <option value="">Todos los proveedores</option>
                  {(proveedoresUnicos || []).map(p => (
                    <option key={p.id} value={p.id}>{p.nombre}</option>
                  ))}
                </select>
              </div>
              {(busqueda || proveedorFiltro) && (
                <button
                  onClick={() => { setBusqueda(''); setProveedorFiltro(''); }}
                  className="px-2.5 py-1.5 text-xs text-gray-600 border border-gray-200 rounded hover:bg-gray-50 flex items-center gap-1 bg-white"
                  title="Limpiar filtros"
                >
                  <X size={12}/> Limpiar
                </button>
              )}

              {/* Selector de vista Unidades / Cajas.
                  Disponible para TODOS los clientes y TODAS las filas: la
                  conversión a cajas depende solo del factor de envase de cada
                  producto, no de que esté en el listado de gestión por unidades.
                  Los colores de marca van en estilo INLINE con theme.primary a
                  propósito: las clases de color de Tailwind se purgan en el
                  build y el botón activo quedaría invisible. */}
              <div
                className="flex items-center rounded border border-gray-200 bg-white overflow-hidden flex-shrink-0"
                role="group"
                aria-label="Unidad de medida del listado"
              >
                <span className="px-2 text-[11px] text-gray-500 border-r border-gray-200 self-stretch flex items-center">
                  Ver en
                </span>
                {[
                  { modo: 'unidades', etiqueta: 'Unidades', ayuda: 'Muestra el stock tal y como está guardado: en unidades sueltas. Es el único modo en el que se puede editar.' },
                  { modo: 'cajas',    etiqueta: 'Cajas',    ayuda: 'Divide cada valor por el contenido del envase del producto. Solo lectura.' },
                ].map(({ modo, etiqueta, ayuda }) => {
                  const activo = vistaStock === modo;
                  const bloqueado = modo === 'cajas' && hayCambios;
                  return (
                    <button
                      key={modo}
                      type="button"
                      onClick={() => cambiarVista(modo)}
                      aria-pressed={activo}
                      title={bloqueado
                        ? 'Tienes cambios sin guardar. Guárdalos o descártalos para ver el stock en cajas.'
                        : ayuda}
                      className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                        activo ? 'text-white' : 'text-gray-600 hover:bg-gray-50'
                      } ${bloqueado ? 'opacity-50 cursor-not-allowed' : ''}`}
                      style={activo ? { backgroundColor: theme.primary } : undefined}
                    >
                      {etiqueta}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Zona scrolleable de la tabla */}
            <div className="flex-1 overflow-y-auto min-h-0">
        {loading ? (
          <div className="p-12 text-center text-gray-400">
            <Loader size={24} className="animate-spin mx-auto" />
          </div>
        ) : pageItems.length === 0 ? (
          <div className="p-12 text-center text-gray-400 text-sm">
            <Package size={40} className="mx-auto text-gray-300 mb-2" />
            {busqueda
              ? 'Sin resultados para esa búsqueda'
              : productos.length === 0
                ? 'Sin productos en el catálogo de este cliente'
                : 'Sin productos visibles'}
          </div>
        ) : (
          <>
          {/* Leyenda del distintivo UDS. Solo se pinta si HAY algún producto
              gestionado por unidades en la página actual: si no, sería una
              explicación de algo que no se ve.
              El color va en estilo INLINE con theme.primary a propósito: las
              clases de color de Tailwind se purgan en el build de producción y
              el distintivo quedaría invisible. */}
          {pageItems.some(p => p.mostrar_en_unidades) && (
            <div className="px-3 py-2 border-b border-gray-200 bg-gray-50 text-xs text-gray-600 flex items-center gap-2">
              <span
                className="px-1.5 py-0.5 rounded text-[10px] font-bold border flex-shrink-0"
                style={{ color: theme.primary, borderColor: theme.primary, backgroundColor: theme.primaryLight }}
              >
                UDS
              </span>
              <span>
                Producto <strong>gestionado por unidades</strong>: se cuenta pieza a pieza, no por
                envases. En estos productos el <strong>Mínimo y el Máximo vienen de la configuración
                del cliente, van en unidades y no se editan aquí</strong> — se ven igual en las dos
                vistas. La <strong>Cantidad</strong> sí se ajusta, y es lo único que cambia al pasar
                a la vista <strong>Cajas</strong>.
              </span>
            </div>
          )}
          {/* Aviso de la vista en cajas. Solo aparece en modo cajas: en modo
              unidades no hay nada que advertir, la pantalla se comporta como
              siempre y es editable. */}
          {enCajas && (
            <div className="px-3 py-2 bg-amber-50 border-b border-amber-200 text-xs text-amber-900 flex items-start gap-2">
              <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
              <span>
                <strong>Vista en cajas: solo cambia la columna Cantidad.</strong> Se muestra dividida
                por el contenido del envase y <strong>se puede editar</strong>: lo que escribas en
                cajas se convierte y se guarda en unidades sueltas, que es como almacena el stock
                (2,5 cajas de 100 = 250 unidades). El <strong>Mínimo y el Máximo van siempre en
                unidades</strong> y se ven igual que en la otra vista — son los umbrales
                configurados para el cliente, no cambian de unidad. Los productos{' '}
                <span className="text-gray-500">en gris</span> no tienen equivalencia de envase
                definida, así que su cantidad no cambia.
              </span>
            </div>
          )}
          <table className="w-full text-sm table-fixed">
            <thead className="bg-gray-50 border-b sticky top-0 z-10">
              <tr>
                <th className="text-left px-3 py-2.5 w-[28%] font-semibold text-gray-700">Producto</th>
                <th className="text-left px-3 py-2.5 w-[12%] font-semibold text-gray-700">Ref.</th>
                <th className="text-left px-3 py-2.5 w-[14%] font-semibold text-gray-700">Proveedor</th>
                {/* La unidad va en la cabecera para que un pantallazo del listado
                    no sea ambiguo: un "14" suelto no dice si son cajas o uds.
                    SOLO Cantidad cambia de unidad con el selector. Mínimo y
                    Máximo llevan "(uds)" FIJO en los dos modos, precisamente
                    para dejar claro que esos umbrales nunca se convierten. */}
                <th className="text-right px-3 py-2.5 w-[10%] font-semibold text-gray-700">Cantidad{enCajas ? ' (cajas)' : ''}</th>
                <th className="text-right px-3 py-2.5 w-[10%] font-semibold text-gray-700">Mínimo (uds)</th>
                <th className="text-right px-3 py-2.5 w-[10%] font-semibold text-gray-700">Máximo (uds)</th>
                <th className="text-left px-3 py-2.5 w-[10%] font-semibold text-gray-700">Estado</th>
                <th className="text-center px-3 py-2.5 w-[6%] font-semibold text-gray-700"></th>
              </tr>
            </thead>
            <tbody>
              {pageItems.map(p => {
                const cant = valorActual(p, 'cantidad');
                const min  = valorActual(p, 'minimo');
                const max  = valorActual(p, 'maximo');
                const cambioPend = !!cambios[p.producto_id];

                // ── Productos gestionados por unidades ─────────────────────
                // `mostrar_en_unidades` lo decide el BACKEND: el producto está
                // en el listado (`producto.gestion_por_unidades`) Y tiene un
                // factor de envase > 1.
                //
                // OJO — esto NO cambia ningún número. La BD ya guarda unidades
                // sueltas, así que en la vista Unidades estas filas se pintan y
                // se editan igual que el resto. Es SOLO el distintivo visual.
                const enUnidades = !!p.mostrar_en_unidades;
                const factorUds = Number(p.factor_unidad_base) || 1;
                const envase = p.unidadbasica || 'envase';
                // ¿Está el producto EN EL LISTADO? Si lo está, su `minimo` y su
                // `maximo` salen del Excel del cliente y se bloquean SIEMPRE,
                // también en modo Unidades.
                //
                // Se mira `gestion_por_unidades` (la marca) y no
                // `mostrar_en_unidades` (marca + factor > 1): un producto del
                // listado sin factor conocido no lleva distintivo UDS, pero sus
                // mínimos vienen del Excel exactamente igual y también se
                // bloquean. Son dos preguntas distintas: una es "¿cómo se pinta?"
                // y la otra "¿quién manda sobre este dato?".
                const delListado = Number(p.gestion_por_unidades) === 1;
                // Sin equivalencia de envase (`factor_unidad_base` = 1): en modo
                // cajas su valor se muestra IGUAL que en unidades, porque no hay
                // nada por lo que dividir. Se marca en gris para que el usuario
                // entienda por qué ese número no cambió.
                const sinFactor = factorDe(p) <= 1;
                // Coercionamos a número: los inputs pueden traer '' o '0' como string.
                const nCant = Number(cant) || 0;
                const nMin  = Number(min)  || 0;
                const nMax  = Number(max)  || 0;
                let estado = 'ok';
                if (nCant === 0 && nMin === 0 && nMax === 0) estado = 'sindatos';
                else if (nMin > 0 && nCant < nMin) estado = 'bajo';
                else if (nMax > 0 && nCant > nMax) estado = 'sobre';

                return (
                  <tr key={p.producto_id} className={`border-t hover:bg-gray-50 ${
                    cambioPend ? 'bg-amber-50' : ''
                  }`}>
                    <td className="px-3 py-2 text-gray-800 font-medium whitespace-normal break-words min-w-[220px]" title={p.nombre}>
                      {/* NOMBRE COMPLETO: antes esta celda usaba `truncate` (una
                          sola linea con "..."), lo que ocultaba el final de
                          nombres largos como "BATA CON SOLAPA COLOR BLANCO T.L
                          BLOOME BY EGOS" o "CASACA ELASTICA BOLSILLO...": el
                          usuario no podia distinguir talla/marca de un vistazo.
                          Ahora envuelve a varias lineas (`whitespace-normal
                          break-words`) y siempre se lee entero; el `title` se
                          mantiene como tooltip. */}
                      {/* INDICADOR VISUAL. Color de marca en estilo INLINE:
                          las clases de color de Tailwind se purgan en el build
                          de producción y el distintivo quedaría invisible. */}
                      {enUnidades && (
                        <span
                          className="inline-block mr-1.5 px-1.5 py-0.5 rounded text-[10px] font-bold align-middle border"
                          style={{ color: theme.primary, borderColor: theme.primary, backgroundColor: theme.primaryLight }}
                          title={`Producto gestionado por unidades: se cuenta pieza a pieza, `
                            + `no por envases. Las cifras de esta fila están en unidades sueltas `
                            + `y se editan con normalidad. Equivalencia: 1 ${envase} = ${factorUds} `
                            + `unidades; cambia a la vista "Cajas" para verlas divididas.`}
                        >
                          UDS
                        </span>
                      )}
                      {p.nombre}
                    </td>
                    <td className="px-3 py-2 text-gray-600 font-mono text-xs truncate">{p.ref_proveedor || '—'}</td>
                    <td className="px-3 py-2 text-gray-600 text-xs truncate">{p.proveedor_nombre || '—'}</td>
                    {/* Cantidad / Mínimo / Máximo.
                        La BD guarda estos tres campos EN UNIDADES SUELTAS, y
                        `cambios` también. Siempre.

                        CANTIDAD — editable en LOS DOS modos:
                          · En Unidades se teclean unidades y se guardan tal cual.
                          · En Cajas se teclean cajas y `onCellChange` las
                            multiplica por el factor antes de guardarlas.
                        Es la misma cifra vista de dos formas, y por eso se
                        sincronizan solas: como `cambios` está en unidades y
                        `mostrarValor` divide al pintar, al cambiar de vista el
                        número aparece ya convertido. No hay estado duplicado.

                        MÍNIMO / MÁXIMO — NO se convierten NUNCA. Son umbrales de
                        reposición que el cliente define en unidades en su Excel:
                        configuración, no una magnitud que cambie de vista. En
                        modo Cajas se muestran EXACTAMENTE igual que en Unidades.
                        Solo son de solo lectura si el producto está en el
                        LISTADO (los fija el Excel y la pantalla no es la
                        herramienta para cambiarlos). Fuera del listado se editan
                        en los DOS modos: como no hay conversión, la vista de
                        cajas no añade ningún riesgo sobre ellos.

                        `sinFactor` = producto sin equivalencia de envase: su
                        Cantidad no cambia entre modos (dividir por 1 no hace
                        nada), y se pinta en gris cuando está bloqueado para que
                        se entienda por qué. */}
                    {['cantidad', 'minimo', 'maximo'].map(campo => {
                      const esMinMax = campo === 'minimo' || campo === 'maximo';
                      const fijadoPorExcel = delListado && esMinMax;
                      const bloqueado = fijadoPorExcel;
                      // Decimales SOLO en la Cantidad en modo cajas: 0,07 cajas
                      // es legítimo. Los umbrales van en unidades enteras.
                      const admiteDecimales = enCajas && campo === 'cantidad';
                      return (
                      <td key={campo} className="px-2 py-1.5 text-right">
                        <input
                          type="number"
                          min="0"
                          step={admiteDecimales ? 'any' : '1'}
                          value={mostrarValor(p, campo)}
                          readOnly={bloqueado}
                          tabIndex={bloqueado ? -1 : undefined}
                          onChange={bloqueado
                            ? undefined
                            : (e) => onCellChange(p.producto_id, campo, e.target.value, factorDe(p))}
                          title={esMinMax
                            ? (fijadoPorExcel
                                ? `${campo === 'minimo' ? 'Mínimo' : 'Máximo'} fijado en la configuración `
                                  + `de mínimos y máximos del cliente, EN UNIDADES. Este producto está `
                                  + `en el listado de gestión por unidades, así que su valor viene de `
                                  + `ahí y no se edita desde esta pantalla. No cambia con la vista.`
                                : tooltipUmbral(campo))
                            : (enCajas ? tooltipCajas(p) : undefined)}
                          className={`w-20 px-2 py-1 text-right border-2 rounded text-sm focus:outline-none transition-colors ${
                            bloqueado
                              ? `bg-gray-100 cursor-default ${(enCajas && sinFactor) ? 'text-gray-400' : 'text-gray-500'}`
                              : ''
                          }`}
                          style={{ borderColor: (cambioPend && !bloqueado) ? theme.primary : '#e5e7eb' }}
                        />
                      </td>
                      );
                    })}
                    <td className="px-3 py-2">
                      {estado === 'bajo' && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-xs font-medium">
                          <TrendingDown size={10} /> Bajo mínimo
                        </span>
                      )}
                      {estado === 'sobre' && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-medium">
                          <TrendingUp size={10} /> Sobrestock
                        </span>
                      )}
                      {estado === 'ok' && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-medium">
                          <Check size={10} /> OK
                        </span>
                      )}
                      {estado === 'sindatos' && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 text-xs font-medium">
                          Producto sin datos
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-1.5 text-center">
                      {/* Botón "ver lotes". El GLIFO cambia según el formato
                          real del producto: el icono de caja en TODAS las filas
                          daba a entender que todo viene en caja. Mismo color,
                          misma posición y misma acción — solo cambia el dibujo
                          y el tooltip.
                          Con envase conocido (factor > 1) → Package (caja).
                          Sin envase definido            → CircleDot (una pieza
                          suelta). Ojo: `factorUds` es 1 tanto si el producto se
                          vende de verdad por unidad como si NO SABEMOS su
                          equivalencia; el tooltip se queda en la lectura
                          operativa, que es la que le sirve a quien cuenta. */}
                      {(esPicking || esAlbaran || esMixto) && (
                        <button
                          onClick={() => onAbrirLotesProducto(p)}
                          className="p-1.5 text-teal-600 hover:bg-teal-50 rounded"
                          title={factorUds > 1
                            ? `Ver lotes · viene en envases de ${factorUds} unidades`
                            : 'Ver lotes · se vende por unidad individual'}
                        >
                          {factorUds > 1 ? <Package size={15} /> : <CircleDot size={15} />}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </>
        )}
            </div>
            {/* Footer fijo del card: paginación */}
            {pageItems.length > 0 && (
              <div className="border-t px-4 py-2 flex items-center justify-between bg-gray-50 text-xs flex-shrink-0">
                <div className="text-gray-600">
                  Mostrando <strong>{pageStart + 1}</strong>–<strong>{Math.min(pageStart + PAGE_SIZE, productosFiltrados.length)}</strong> de <strong>{productosFiltrados.length}</strong>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-2 py-1 rounded border border-gray-300 disabled:opacity-40 flex items-center gap-1 bg-white"
                  ><ChevronLeft size={14}/> Anterior</button>
                  <span className="px-3">Página {page} / {totalPages}</span>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="px-2 py-1 rounded border border-gray-300 disabled:opacity-40 flex items-center gap-1 bg-white"
                  >Siguiente <ChevronRight size={14}/></button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};


// ─── Panel de pendientes de verificación ─────────────────────────────────────
const PendientesVerificacionPanel = ({ pendientes, theme, onAceptar, onRechazar }) => {
  if (!pendientes || pendientes.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm border p-12 text-center">
        <Check size={36} className="mx-auto text-emerald-400 mb-3" />
        <p className="text-sm text-gray-600 font-semibold">Sin pendientes de verificación</p>
        <p className="text-xs text-gray-400 mt-1">
          Cuando un proveedor añada un albarán, las cantidades aparecerán aquí para que las verifiques antes de sumarlas al stock.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
      <div className="p-4 border-b bg-amber-50">
        <h3 className="text-sm font-bold text-amber-900 flex items-center gap-2">
          <AlertTriangle size={16} />
          Pendientes de verificación ({pendientes.length})
        </h3>
        <p className="text-xs text-amber-700 mt-1">
          Estas cantidades fueron declaradas por proveedores al subir albarán. Acepta tras verificar que el material llegó realmente, o rechaza si no aplica.
        </p>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-gray-50 border-b">
          <tr>
            <th className="text-left px-4 py-2 font-semibold text-gray-700">Producto</th>
            <th className="text-left px-3 py-2 font-semibold text-gray-700">Pedido</th>
            <th className="text-left px-3 py-2 font-semibold text-gray-700">Albarán</th>
            <th className="text-left px-3 py-2 font-semibold text-gray-700">Unidad base</th>
            <th className="text-right px-3 py-2 font-semibold text-gray-700">Cant. pedida</th>
            <th className="text-right px-3 py-2 font-semibold text-gray-700">A sumar</th>
            <th className="text-left px-3 py-2 font-semibold text-gray-700">Creado por</th>
            <th className="text-center px-3 py-2 font-semibold text-gray-700">Acciones</th>
          </tr>
        </thead>
        <tbody>
          {pendientes.map(p => (
            <tr key={p.id} className="border-t hover:bg-gray-50">
              <td className="px-4 py-2 text-gray-800 font-medium truncate" title={p.producto_nombre}>
                {p.producto_nombre}
                <div className="text-[10px] text-gray-400 font-mono">{p.ref_proveedor || ''}</div>
              </td>
              <td className="px-3 py-2 text-gray-700 font-mono text-xs">{p.pedido_npedido || `#${p.pedido_id}`}</td>
              <td className="px-3 py-2 text-gray-700 font-mono text-xs">{p.albaran || '—'}</td>
              <td className="px-3 py-2 text-gray-500 text-xs">{p.unidadbasica || '—'}</td>
              <td className="px-3 py-2 text-right">{p.cantidad_pedida}</td>
              <td className="px-3 py-2 text-right font-bold" style={{ color: theme.primary }}>{p.unidades_a_sumar}</td>
              <td className="px-3 py-2 text-gray-500 text-xs">{p.creado_por_nombre || '—'}</td>
              <td className="px-3 py-2 text-center">
                <div className="flex items-center justify-center gap-1">
                  <button
                    onClick={() => onAceptar(p)}
                    className="px-2 py-1 text-[11px] font-semibold text-white rounded transition-colors"
                    style={{ backgroundColor: theme.primary }}
                    title="Aceptar y sumar al stock"
                  >Aceptar</button>
                  <button
                    onClick={() => onRechazar(p)}
                    className="px-2 py-1 text-[11px] font-semibold text-red-700 bg-red-50 hover:bg-red-100 rounded"
                    title="Rechazar (no se suma)"
                  >Rechazar</button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};


// ─── Panel de albaranes pendientes (modo 'mixto') ────────────────────────────
// Lista los albaranes en estado 'pendiente' que el cliente debe aceptar o
// denegar. Click sobre un albarán abre AlbaranMixtoAceptarModal para revisar
// líneas, ajustar cantidades y resolver.
const AlbaranesMixtoPanel = ({ albaranes, theme, onAbrirAlbaran }) => {
  if (!albaranes || albaranes.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm border p-12 text-center">
        <Check size={36} className="mx-auto text-emerald-400 mb-3" />
        <p className="text-sm text-gray-600 font-semibold">Sin albaranes pendientes</p>
        <p className="text-xs text-gray-400 mt-1">
          Cuando el proveedor o el admin carguen un albarán para tus pedidos, aparecerá aquí para que lo verifiques antes de aplicarlo al stock.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
      <div className="p-4 border-b bg-amber-50">
        <h3 className="text-sm font-bold text-amber-900 flex items-center gap-2">
          <FileText size={16} />
          Albaranes pendientes de aceptación ({albaranes.length})
        </h3>
        <p className="text-xs text-amber-700 mt-1">
          Doble verificación: el stock NO se actualiza hasta que aceptes cada albarán. Puedes editar cantidades si lo que recibiste no coincide con lo declarado.
        </p>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-gray-50 border-b">
          <tr>
            <th className="text-left px-4 py-2 font-semibold text-gray-700">Nº Albarán</th>
            <th className="text-left px-3 py-2 font-semibold text-gray-700">Pedido</th>
            <th className="text-left px-3 py-2 font-semibold text-gray-700">Proveedor</th>
            <th className="text-left px-3 py-2 font-semibold text-gray-700">Cargado por</th>
            <th className="text-left px-3 py-2 font-semibold text-gray-700">Fecha</th>
            <th className="text-right px-3 py-2 font-semibold text-gray-700">Líneas</th>
            <th className="px-3 py-2"></th>
          </tr>
        </thead>
        <tbody>
          {albaranes.map(a => (
            <tr key={a.id} className="border-b hover:bg-gray-50">
              <td className="px-4 py-2 font-bold text-gray-800">{a.numero}</td>
              <td className="px-3 py-2 text-gray-700">{a.npedido || `#${a.pedido_id}`}</td>
              <td className="px-3 py-2 text-gray-600">{a.proveedor_nombre || '—'}</td>
              <td className="px-3 py-2 text-gray-600 text-xs">{a.created_by_nombre || '—'}</td>
              <td className="px-3 py-2 text-gray-600 text-xs">
                {a.created_at ? new Date(a.created_at).toLocaleDateString('es-ES') : '—'}
              </td>
              <td className="px-3 py-2 text-right">
                <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-gray-100 text-gray-700">
                  {a.total_lineas}
                </span>
              </td>
              <td className="px-3 py-2 text-right">
                <button
                  onClick={() => onAbrirAlbaran(a.pedido_id)}
                  className="px-3 py-1 text-xs font-semibold text-white rounded transition-colors"
                  style={{ backgroundColor: theme.primary }}
                  title="Revisar y aceptar/denegar"
                >
                  Revisar
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};


// ─── Modal para aceptar pendiente con factor editable ────────────────────────
const AceptarPendienteModal = ({ pendiente, theme, onClose, onApplied }) => {
  const [factor, setFactor] = useState(pendiente?.factor_propuesto || 1);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (pendiente) {
      setFactor(pendiente.factor_propuesto || 1);
      setError('');
    }
  }, [pendiente]);

  if (!pendiente) return null;

  const unidadesFinales = pendiente.cantidad_pedida * factor;

  const handleAceptar = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API_URL}/api/stock/pendiente-verificacion/${pendiente.id}/aceptar`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ unidades_finales: unidadesFinales }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.message || 'Error al aceptar');
      onApplied?.(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
        <div className="px-6 py-4 border-b" style={{ backgroundColor: theme.primaryLight }}>
          <h2 className="text-lg font-bold text-gray-800">Verificar antes de sumar al stock</h2>
          <p className="text-xs text-gray-600 mt-0.5">
            Pedido <strong className="font-mono">{pendiente.pedido_npedido || `#${pendiente.pedido_id}`}</strong>
            {' · '}Albarán <strong className="font-mono">{pendiente.albaran || '—'}</strong>
          </p>
        </div>
        <div className="p-6 space-y-3">
          <div>
            <div className="text-xs text-gray-500 uppercase font-semibold">Producto</div>
            <div className="text-sm font-bold text-gray-800">{pendiente.producto_nombre}</div>
            <div className="text-xs text-gray-500">Ref: {pendiente.ref_proveedor || '—'}</div>
          </div>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <div className="text-xs text-gray-500 uppercase font-semibold">Unidad base detectada</div>
              <div className="text-gray-700 italic">{pendiente.unidadbasica || '—'}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500 uppercase font-semibold">Cantidad pedida</div>
              <div className="text-gray-700 font-mono">{pendiente.cantidad_pedida}</div>
            </div>
          </div>
          <div className="border-t pt-3">
            <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Unidades por embalaje</label>
            <input
              type="number"
              min="1"
              value={factor}
              onChange={(e) => setFactor(Math.max(1, parseInt(e.target.value) || 1))}
              className="w-32 px-3 py-2 border-2 border-gray-200 rounded-lg text-sm focus:outline-none"
              onFocus={(e) => e.target.style.borderColor = theme.primary}
              onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
            />
            <p className="text-xs text-gray-500 mt-1">Editable para envíos parciales o si la unidad base venía mal detectada.</p>
          </div>
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
            <div className="text-xs text-emerald-700 uppercase font-semibold">Total a sumar al stock</div>
            <div className="text-2xl font-bold" style={{ color: theme.primary }}>{unidadesFinales} unidades</div>
            <div className="text-xs text-emerald-700 mt-1">
              {pendiente.cantidad_pedida} × {factor} = {unidadesFinales}
            </div>
          </div>
          {error && <div className="p-2 bg-red-50 border border-red-200 text-red-700 text-sm rounded">{error}</div>}
        </div>
        <div className="px-6 py-4 bg-gray-50 border-t flex items-center justify-end gap-3">
          <button onClick={onClose} disabled={saving} className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-200 rounded-lg">Cancelar</button>
          <button
            onClick={handleAceptar}
            disabled={saving || unidadesFinales <= 0}
            className="px-5 py-2 text-sm font-semibold text-white rounded-lg disabled:opacity-50 flex items-center gap-2"
            style={{ backgroundColor: theme.primary }}
          >
            {saving ? <Loader size={15} className="animate-spin" /> : <Check size={15} />}
            Confirmar y sumar
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Modal único de Excel: descargar plantilla + subir + preview + aplicar ───
const ExcelModal = ({ open, onClose, cliente, theme, onApplied }) => {
  const [step, setStep] = useState('idle'); // idle | uploading | preview | applying
  const [preview, setPreview] = useState(null);
  const [confirmed, setConfirmed] = useState({}); // { producto_id: true } para pendientes
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (open) { setStep('idle'); setPreview(null); setConfirmed({}); setError(''); }
  }, [open]);

  if (!open) return null;

  const handleDownload = async () => {
    if (!cliente) return;
    setDownloading(true);
    try {
      const r = await fetch(`${API_URL}/api/stock/cliente/${cliente.id}/excel`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (!r.ok) throw new Error('Error al descargar plantilla');
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `stock_${(cliente.razon_social || 'cliente').replace(/[^a-zA-Z0-9]/g, '_')}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message);
    } finally {
      setDownloading(false);
    }
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !cliente) return;
    setStep('uploading');
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await fetch(`${API_URL}/api/stock/cliente/${cliente.id}/excel/preview`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: fd,
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.message || 'Error al procesar Excel');
      setPreview(data);
      // Default: ningun pendiente marcado (mas seguro)
      setConfirmed({});
      setStep('preview');
    } catch (e) {
      setError(e.message);
      setStep('idle');
    }
  };

  const togglePendiente = (id) => setConfirmed(prev => ({ ...prev, [id]: !prev[id] }));
  const todoConfirmado = preview && preview.pendientes.length > 0 &&
    preview.pendientes.every(p => confirmed[p.producto_id]);

  const handleApply = async () => {
    if (!preview) return;
    setStep('applying');
    try {
      const cambios = [
        ...preview.automaticos.map(p => ({ producto_id: p.producto_id, ...p.nuevo, motivo: 'Importacion Excel' })),
        ...preview.pendientes
          .filter(p => confirmed[p.producto_id])
          .map(p => ({ producto_id: p.producto_id, ...p.nuevo, motivo: 'Importacion Excel (confirmado)' })),
      ];
      if (cambios.length === 0) {
        setError('No hay cambios para aplicar');
        setStep('preview');
        return;
      }
      const r = await fetch(`${API_URL}/api/stock/cliente/${cliente.id}/bulk`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ cambios }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.message || 'Error al aplicar');
      onApplied?.(data);
      onClose();
    } catch (e) {
      setError(e.message);
      setStep('preview');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">

        <div className="flex items-center justify-between px-6 py-4 border-b" style={{ backgroundColor: theme.primaryLight }}>
          <div className="flex items-center gap-3">
            <FileSpreadsheet size={20} style={{ color: theme.primary }} />
            <div>
              <h2 className="text-lg font-bold text-gray-800">Importar / Exportar Excel</h2>
              <p className="text-xs text-gray-600">{cliente?.razon_social}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white rounded-lg" disabled={step === 'applying' || step === 'uploading'}>
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">

          {step === 'idle' && (
            <>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="border-2 border-gray-200 rounded-xl p-5">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="p-2 rounded-lg" style={{ backgroundColor: theme.primaryLight }}>
                      <Download size={20} style={{ color: theme.primary }} />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-800">1. Descargar plantilla</h3>
                      <p className="text-xs text-gray-500 mt-1">El Excel viene precargado con el catálogo y stock actual.</p>
                    </div>
                  </div>
                  <button
                    onClick={handleDownload}
                    disabled={downloading}
                    className="w-full px-4 py-2.5 text-white text-sm font-semibold rounded-lg flex items-center justify-center gap-2 disabled:opacity-50"
                    style={{ backgroundColor: theme.primary }}
                  >
                    {downloading ? <Loader size={16} className="animate-spin" /> : <Download size={16} />}
                    Descargar plantilla
                  </button>
                </div>

                <div className="border-2 border-gray-200 rounded-xl p-5">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="p-2 rounded-lg" style={{ backgroundColor: theme.primaryLight }}>
                      <Upload size={20} style={{ color: theme.primary }} />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-800">2. Subir Excel modificado</h3>
                      <p className="text-xs text-gray-500 mt-1">Edita las columnas <strong>cantidad</strong>, <strong>minimo</strong> y <strong>maximo</strong>.</p>
                    </div>
                  </div>
                  <label className="w-full px-4 py-2.5 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center gap-2 cursor-pointer hover:bg-gray-50 text-sm text-gray-700">
                    <Upload size={16} />
                    Elegir archivo .xlsx
                    <input type="file" accept=".xlsx" className="hidden" onChange={handleFile} />
                  </label>
                </div>
              </div>

              <div className="mt-5 p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
                <div className="font-semibold mb-1">¿Cómo funciona la verificación?</div>
                <ul className="text-xs space-y-1 list-disc pl-4">
                  <li>Los cambios "seguros" (subir cantidad, ajustar mínimos) se aplican automáticamente.</li>
                  <li>Cuando un valor pasa de <strong>X &gt; 0 a 0</strong> en cantidad, mínimo o máximo, requiere confirmación manual (puede ser un olvido en la celda).</li>
                </ul>
              </div>
            </>
          )}

          {step === 'uploading' && (
            <div className="p-12 text-center text-gray-500">
              <Loader size={32} className="animate-spin mx-auto mb-3" />
              <p className="text-sm">Analizando el Excel...</p>
            </div>
          )}

          {step === 'preview' && preview && (
            <>
              <div className="grid grid-cols-4 gap-3 mb-4">
                <Stat label="Filas leídas" value={preview.total_filas} />
                <Stat label="Automáticos" value={preview.automaticos.length} color={theme.primary} />
                <Stat label="Pendientes" value={preview.pendientes.length} color="#d97706" />
                <Stat label="Sin cambios" value={preview.sin_cambios} muted />
              </div>

              {preview.automaticos.length > 0 && (
                <div className="mb-5">
                  <h3 className="text-sm font-bold text-gray-800 mb-2 flex items-center gap-2">
                    <Check size={14} style={{ color: theme.primary }} />
                    Cambios automáticos ({preview.automaticos.length})
                  </h3>
                  <div className="border rounded-lg overflow-hidden max-h-64 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="text-left px-3 py-2">Producto</th>
                          <th className="text-right px-3 py-2">Cant.</th>
                          <th className="text-right px-3 py-2">Mín.</th>
                          <th className="text-right px-3 py-2">Máx.</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.automaticos.map(p => (
                          <tr key={p.producto_id} className="border-t">
                            <td className="px-3 py-1.5 truncate max-w-[300px]" title={p.nombre}>{p.nombre}</td>
                            <td className="px-3 py-1.5 text-right">{renderChange(p.actual.cantidad, p.nuevo.cantidad)}</td>
                            <td className="px-3 py-1.5 text-right">{renderChange(p.actual.minimo, p.nuevo.minimo)}</td>
                            <td className="px-3 py-1.5 text-right">{renderChange(p.actual.maximo, p.nuevo.maximo)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {preview.pendientes.length > 0 && (
                <div className="mb-2">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2">
                      <AlertTriangle size={14} className="text-amber-600" />
                      Requieren verificación manual ({preview.pendientes.length})
                    </h3>
                    <label className="text-xs text-gray-600 flex items-center gap-1 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={todoConfirmado}
                        onChange={() => {
                          const all = !todoConfirmado;
                          const next = {};
                          preview.pendientes.forEach(p => { next[p.producto_id] = all; });
                          setConfirmed(next);
                        }}
                      /> Marcar todos
                    </label>
                  </div>
                  <p className="text-xs text-gray-500 mb-2">
                    Estos cambios mandan valores a 0 desde un valor mayor. Marca solo los que confirmes manualmente.
                  </p>
                  <div className="border-2 border-amber-300 rounded-lg overflow-hidden max-h-64 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-amber-50 sticky top-0">
                        <tr>
                          <th className="text-center px-2 py-2 w-10">✓</th>
                          <th className="text-left px-3 py-2">Producto</th>
                          <th className="text-right px-3 py-2">Cant.</th>
                          <th className="text-right px-3 py-2">Mín.</th>
                          <th className="text-right px-3 py-2">Máx.</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.pendientes.map(p => (
                          <tr key={p.producto_id} className="border-t">
                            <td className="px-2 py-1.5 text-center">
                              <input
                                type="checkbox"
                                checked={!!confirmed[p.producto_id]}
                                onChange={() => togglePendiente(p.producto_id)}
                              />
                            </td>
                            <td className="px-3 py-1.5 truncate max-w-[280px]" title={p.nombre}>{p.nombre}</td>
                            <td className={`px-3 py-1.5 text-right ${p.flags.cantidad ? 'bg-amber-50 font-bold' : ''}`}>{renderChange(p.actual.cantidad, p.nuevo.cantidad)}</td>
                            <td className={`px-3 py-1.5 text-right ${p.flags.minimo ? 'bg-amber-50 font-bold' : ''}`}>{renderChange(p.actual.minimo, p.nuevo.minimo)}</td>
                            <td className={`px-3 py-1.5 text-right ${p.flags.maximo ? 'bg-amber-50 font-bold' : ''}`}>{renderChange(p.actual.maximo, p.nuevo.maximo)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {preview.automaticos.length === 0 && preview.pendientes.length === 0 && (
                <div className="p-8 text-center text-gray-500 text-sm">
                  El Excel no introduce ningún cambio respecto al estado actual.
                </div>
              )}
            </>
          )}

          {step === 'applying' && (
            <div className="p-12 text-center text-gray-500">
              <Loader size={32} className="animate-spin mx-auto mb-3" />
              <p className="text-sm">Aplicando cambios...</p>
            </div>
          )}

          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg">
              {error}
            </div>
          )}
        </div>

        {step === 'preview' && preview && (
          <div className="px-6 py-4 bg-gray-50 border-t flex items-center justify-end gap-3">
            <button
              onClick={() => { setStep('idle'); setPreview(null); }}
              className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-200 rounded-lg"
            >Volver</button>
            <button
              onClick={handleApply}
              disabled={preview.automaticos.length === 0 && Object.values(confirmed).filter(Boolean).length === 0}
              className="px-5 py-2 text-sm font-semibold text-white rounded-lg disabled:opacity-50 flex items-center gap-2"
              style={{ backgroundColor: theme.primary }}
            >
              <Check size={16} />
              Aplicar cambios ({preview.automaticos.length + Object.values(confirmed).filter(Boolean).length})
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const Stat = ({ label, value, color, muted }) => (
  <div className={`rounded-lg p-3 text-center border ${muted ? 'bg-gray-50 border-gray-200' : 'bg-white border-gray-200'}`}>
    <div className="text-xl font-bold" style={{ color: color || (muted ? '#6b7280' : '#111827') }}>{value}</div>
    <div className="text-[10px] text-gray-500 uppercase mt-0.5">{label}</div>
  </div>
);

const renderChange = (antes, despues) => {
  if (despues === undefined) return <span className="text-gray-400">—</span>;
  return (
    <span>
      <span className="text-gray-400">{antes}</span>
      <span className="text-gray-400 mx-1">→</span>
      <span className="font-bold">{despues}</span>
    </span>
  );
};


const Toast = ({ toast, theme }) => (
  <div
    className="fixed bottom-6 right-6 z-50 max-w-sm px-4 py-3 rounded-lg shadow-xl text-white"
    style={{ backgroundColor: toast.type === 'success' ? theme.primary : '#dc2626' }}
  >
    <div className="flex items-start gap-2">
      {toast.type === 'success' ? <Check size={18}/> : <AlertCircle size={18}/>}
      <span className="text-sm font-medium">{toast.msg}</span>
    </div>
  </div>
);
